import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import api from '../services/api';
import statutsParkingApi from '../services/statuts-parking';
import { referentiels } from '../services/referentiels';
import ORModal from './ORModal';
import { LitigeDetailsModal } from './Litiges';
import litigesApi from '../services/litiges';
import ParkingCarteView from './ParkingCarteView';
import ParkingRapportView from './ParkingRapportView';
import { PlanificationCell, useDossierPlanning } from './shared/DossierPlanification';
import { BoutonPanneau, Rubrique, ChoixPastilles, ChoixMultiples, Interrupteur, Pastilles, IconeFiltres, IconeCalendrier } from './shared/FiltresBarre';
import { libelleStatutDepuis } from '../utils/statutAtelier';
import ParkingPrint from './ParkingPrint';
import carnetCommandeApi from '../services/carnet-commande';
import permissionsApi from '../services/permissions';
import InterventionModal from './InterventionModal';
import './GestionParking.css';

import { texteTheme } from '../utils/theme';
import FactureDetailModal from './shared/FactureDetailModal';
const EMPTY_FORM = { agent: '', allee: '', identifiant: '', type_identifiant: 'PLAQUE', marque: '', confidence: '' };

const FILTERS_KEY = 'parking_filters';

/* ── Export Excel : champs disponibles + sélection persistée ── */
const EXPORT_COLUMN_DEFS = [
  ['identifiant',     'Identifiant'],
  ['marque',          'Marque'],
  ['allee',           'Allée'],
  ['statut',          'Statut'],
  ['concession',      'Concession'],
  ['agent',           'Responsable'],
  ['proprietaire',    'Propriétaire'],
  ['nom_client',      'Client'],
  ['created_at',      'Détection'],
  ['updated_at',      'Dernier relevé'],
  ['statut_commande', 'Pièces'],
  ['planning_statut', 'Statut atelier (Planning)'],
  ['planning_statut_since', 'Statut atelier depuis le'],
  ['has_vo',          'VO'],
  ['litige',          'Litige'],
  ['lead',            'Lead'],
  ['or_en_cours',     'OR en cours'],
  ['num_or',          "Numéro d'OR"],
  ['or_date_creation', "Date d'ouverture d'OR"],
  ['notes',           'Notes'],
  ['latitude',        'Latitude'],
  ['longitude',       'Longitude'],
];
const EXPORT_DEFAULT_ON = new Set([
  'identifiant', 'marque', 'allee', 'statut', 'concession', 'agent',
  'proprietaire', 'created_at', 'updated_at', 'has_vo', 'litige', 'lead',
  'or_en_cours', 'num_or', 'or_date_creation', 'notes',
  'planning_statut', 'planning_statut_since',
]);
const EXPORT_FIELDS_KEY = 'parking_export_fields';

function getInitialExportFields() {
  let saved = null;
  try { saved = JSON.parse(localStorage.getItem(EXPORT_FIELDS_KEY)); } catch {}
  const out = {};
  EXPORT_COLUMN_DEFS.forEach(([k]) => {
    out[k] = saved && typeof saved === 'object' && k in saved ? !!saved[k] : EXPORT_DEFAULT_ON.has(k);
  });
  return out;
}

function getInitialFilters() {
  try {
    const saved = localStorage.getItem(FILTERS_KEY);
    if (saved) {
      const p = JSON.parse(saved);
      return {
        search:           p.search           || '',
        plaque:           p.plaque           || '',
        concession:       p.concession       || '',
        zones:            Array.isArray(p.zones) ? p.zones : [],
        statut:           p.statut           || '',
        statutAtelier:    p.statutAtelier    || '',
        pieces:           p.pieces           || '',
        ancienneteActu:   ACTUALISATION_TRANCHES.some(t => t.value === p.ancienneteActu) ? p.ancienneteActu : '',
        responsable:      p.responsable      || '',
        alertes:          p.alertes          || { vo: false, litige: false, lead: false },
      };
    }
  } catch {}
  return {
    search: '', plaque: '', concession: '', zones: [], statut: '', statutAtelier: '', pieces: '', ancienneteActu: '',
    responsable: localStorage.getItem('parking_filter_responsable') || '',
    alertes: { vo: false, litige: false, lead: false },
  };
}

const ANCIENNETE_TRANCHES = [
  { value: 'lt10',  label: '< 10 jours' },
  { value: 'lt30',  label: '10 – 30 jours' },
  { value: 'lt90',  label: '30 – 90 jours' },
  { value: 'gte90', label: '+ 90 jours' },
];

function getAnciennete(created_at) {
  if (!created_at) return { label: '-', value: null, cls: '' };
  const days = Math.floor((Date.now() - new Date(created_at).getTime()) / 86400000);
  if (days < 10)  return { label: `${days}j`,  value: 'lt10',  cls: 'age-lt10' };
  if (days < 30)  return { label: `${days}j`,  value: 'lt30',  cls: 'age-lt30' };
  if (days < 90)  return { label: `${days}j`,  value: 'lt90',  cls: 'age-lt90' };
                  return { label: `${days}j`,  value: 'gte90', cls: 'age-gte90' };
}

const PIECES_FILTRE = [
  { value: 'en_commande',        label: 'Commandé' },
  { value: 'recu_partiellement', label: 'Partiel' },
  { value: 'livre',              label: 'Reçu' },
  { value: 'aucune',             label: 'Sans commande' },
];

const ACTUALISATION_TRANCHES = [
  { value: 'lt1',   label: "Aujourd'hui" },
  { value: 'lt7',   label: '< 7 jours' },
  { value: 'lt30',  label: '7 – 30 jours' },
  { value: 'gte30', label: '+ 30 jours' },
];

// Tranche du dernier relevé (dernier scan sur le parc). À défaut, la date de
// détection : un véhicule vu une seule fois n'avait pas de date de relevé et
// tombait en « + 30 jours » (corrigé le 2026-10-10, avec un rattrapage en base).
function getAncienneteActu(e) {
  const iso = e.updated_at || e.created_at;
  if (!iso) return { value: 'gte30' };
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days < 1)  return { value: 'lt1' };
  if (days < 7)  return { value: 'lt7' };
  if (days < 30) return { value: 'lt30' };
                 return { value: 'gte30' };
}

/* ACTIVE_STYLE supprimé — géré par .page-select.active-filter (shared.css) */

// Colonne Planification : seule une date à venir (aujourd'hui compris) est
// affichée (demande du 2026-10-13) — une date passée ne dit plus quand le
// véhicule est attendu. Le dossier reste ouvrable depuis le statut atelier.
// Jour LOCAL (pas toISOString, qui donne la date UTC : un soir aux Antilles
// serait déjà « demain »).
function jourLocalIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function planificationAVenir(e) {
  const d = e?.date_planification ? String(e.date_planification).slice(0, 10) : '';
  return d && d >= jourLocalIso() ? d : null;
}

function ParkingPhotoBtn({ onClick }) {
  return (
    <button
      className="parking-photo-btn"
      onClick={onClick}
      title="Voir la photo"
    >
      📷
    </button>
  );
}

// ─── Impression du tableau filtré ──────────────────────────────────────────
function ParkingTablePrint({ entries, statuts, responsables, isSuperAdmin, onClose }) {
  useEffect(() => {
    const timer = setTimeout(() => window.print(), 400);
    return () => clearTimeout(timer);
  }, []);

  const fmt = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };
  const getStatut = (val) => statuts.find(s => s.valeur === val);
  const getResp = (id) => {
    const r = responsables.find(r => r.id === id);
    return r ? `${r.nom}${r.prenom ? ' ' + r.prenom : ''}` : '—';
  };
  const getAnc = (created_at) => {
    if (!created_at) return '—';
    const days = Math.floor((Date.now() - new Date(created_at).getTime()) / 86400000);
    if (days < 1) return "Auj.";
    if (days < 7) return `${days}j`;
    if (days < 30) return `${Math.floor(days / 7)} sem.`;
    return `${Math.floor(days / 30)} mois`;
  };

  return (
    <div className="parking-table-print-container">
      <style>{`
        @media screen {
          .parking-table-print-container { position: fixed; inset: 0; z-index: 9999; background: #fff; overflow-y: auto; padding: 20px; }
          .parking-table-print-close { position: fixed; top: 16px; right: 16px; z-index: 10000; padding: 8px 16px; background: #ef4444; color: #fff; border: none; border-radius: 8px; font-weight: 600; cursor: pointer; }
        }
        @media print {
          .parking-table-print-close { display: none !important; }
          body * { visibility: hidden; }
          .parking-table-print-container, .parking-table-print-container * { visibility: visible; }
          .parking-table-print-container { position: absolute; left: 0; top: 0; width: 100%; }
        }
        .ptp-title { text-align: center; font-size: 14px; font-weight: 700; margin-bottom: 8px; color: #1e293b; }
        .ptp-meta { text-align: center; font-size: 10px; color: #64748b; margin-bottom: 12px; }
        .ptp-table { width: 100%; border-collapse: collapse; font-size: 9px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
        .ptp-table th { background: #f1f5f9; font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: #475569; padding: 4px 6px; border-bottom: 2px solid #cbd5e1; text-align: left; white-space: nowrap; }
        .ptp-table td { padding: 3px 6px; border-bottom: 1px solid #e2e8f0; color: #1e293b; vertical-align: middle; }
        .ptp-table tr:nth-child(even) { background: #f8fafc; }
        .ptp-statut-dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 4px; vertical-align: middle; }
        .ptp-plaque { font-weight: 700; font-family: monospace; color: #0891b2; }
      `}</style>

      <button className="parking-table-print-close" onClick={onClose}>✕ Fermer</button>

      <div className="ptp-title">Gestion Parking — {entries.length} véhicule{entries.length !== 1 ? 's' : ''}</div>
      <div className="ptp-meta">Imprimé le {new Date().toLocaleDateString('fr-FR')} à {new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</div>

      <table className="ptp-table">
        <thead>
          <tr>
            <th>Détection</th>
            <th>Véhicule</th>
            <th>Marque</th>
            <th>Client</th>
            <th>Statut</th>
            <th>Ancienneté</th>
            <th>Concession</th>
            <th>Responsable</th>
            <th>OR</th>
            <th>Facture</th>
            <th>Planif.</th>
          </tr>
        </thead>
        <tbody>
          {entries.map(e => {
            const st = getStatut(e.statut);
            return (
              <tr key={e.id}>
                <td style={{ whiteSpace: 'nowrap' }}>{fmt(e.updated_at)}</td>
                <td><span className="ptp-plaque">{e.identifiant || '—'}</span></td>
                <td style={{ textTransform: 'uppercase' }}>{e.marque || '—'}</td>
                <td>{e.nom_client || '—'}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <span className="ptp-statut-dot" style={{ background: st?.couleur || '#94a3b8' }} />
                  {st?.nom || e.statut || '—'}
                </td>
                <td>{getAnc(e.created_at)}</td>
                <td>{e.concession || '—'}</td>
                <td>{getResp(e.responsable_id)}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{e.or_date_creation ? fmt(e.or_date_creation) : '—'}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{e.derniere_facture_date ? fmt(e.derniere_facture_date) : '—'}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{planificationAVenir(e) ? fmt(e.date_planification) : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function GestionParking({ user, onOpenVehicule = () => {}, editMode = false, canDeleteParking = false, setHeaderAction, onCreerLitige = null }) {
  const isOperateur = user?.role === 'operateur';
  const isSuperAdmin = user?.role === 'super-admin';
  // Droit aux actions groupées (sélection / suppression / archivage / statut en
  // masse) : non-opérateurs comme avant, + opérateur explicitement autorisé à
  // supprimer sur la page parking (permissions_pages.peut_supprimer → ex. GUEPIN).
  const canManageBulk = !isOperateur || canDeleteParking;

  // La colonne Planification est visible de TOUS (demande du 2026-08-10), mais
  // la date n'est cliquable que si le compte a accès à la page Planning : la
  // modale qu'elle ouvre est celle du Planning Atelier, et son bouton mène à la
  // grille. Sans accès, la date reste affichée aux couleurs du statut, inerte.
  const [canOpenPlanning, setCanOpenPlanning] = useState(isSuperAdmin);
  useEffect(() => {
    if (isSuperAdmin) return;
    let annule = false;
    permissionsApi.checkAccess('planning')
      .then(r => { if (!annule) setCanOpenPlanning(!!r.data?.hasAccess); })
      .catch(() => {});
    return () => { annule = true; };
  }, [isSuperAdmin]);

  // Lien profond vers la modale d'intervention du Planning Atelier : on dépose
  // l'id à ouvrir puis on bascule de page — le Planning le consomme au montage,
  // se positionne sur la journée du dossier et ouvre la modale (même mécanique
  // différée que sa recherche globale).
  // Le dossier s'ouvre DANS Gestion Parking (demande 2026-08-12) : changer de
  // page pour lire une date de planification etait disproportionne. La modale
  // du Planning Atelier est rendue ici meme ; son bouton « Planning Atelier »
  // reste disponible pour qui veut la grille.
  // Colonne Planification : module partage (sept ecrans, meme rendu, meme
  // modale). `loadPlanningRefs` local reste pour la creation rapide.
  const { ouvrir: ouvrirDossierPlanning, modale: modaleDossier } = useDossierPlanning({
    user, editMode, onEnregistre: () => loadEntries(parkingView === 'archives'),
  });

  // Boutons d'action dans le header
  useEffect(() => {
    if (!setHeaderAction) return;
    const secondary = [];
    if (!isOperateur) {
      secondary.push({ label: '🤖 Analyse', onClick: () => runBatchAnalysis() });
    }
    secondary.push({ label: '🖨️ Imprimer', onClick: () => setPrintMode('tableau') });
    secondary.push({ label: '📊 Export', onClick: () => setShowExportModal(true) });
    setHeaderAction({
      primary: { label: '+ Nouvelle entrée', onClick: () => openAddModal() },
      secondary
    });
  }, [setHeaderAction, isOperateur]); // eslint-disable-line react-hooks/exhaustive-deps

  const [entries, setEntries]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [orModalId, setOrModalId]       = useState(null);
  const [commandeDetailOr, setCommandeDetailOr] = useState(null);
  const [commandeDetailData, setCommandeDetailData] = useState(null);
  const [commandeDetailLoading, setCommandeDetailLoading] = useState(false);
  const [litigeModal, setLitigeModal]   = useState(null);
  const [concessionsList, setConcessionsList] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [filters, setFilters] = useState(getInitialFilters);
  const [sort, setSort] = useState({ col: null, dir: 'asc' });
  const [printMode, setPrintMode] = useState(null); // 'tableau' | 'fiches'
  const [showPrintMenu, setShowPrintMenu] = useState(false);
  const printMenuRef = useRef(null);

  // Filtre zone (liste à cases) — retiré le 08/10, remis le 13/10 (demande
  // utilisateur), y compris dans l'onglet Archives (même barre de filtres).

  // Export Excel (modale choix des champs)
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFields, setExportFields] = useState(getInitialExportFields);

  // Édition inline nom_client
  const [nomClientEdit, setNomClientEdit] = useState(null); // id
  const [nomClientVal, setNomClientVal]   = useState('');

  // Ajout manuel
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData]         = useState(EMPTY_FORM);
  const [formSaving, setFormSaving]     = useState(false);
  const [formError, setFormError]       = useState('');

  // Import Excel
  const [showImportModal, setShowImportModal] = useState(false);
  const [importStep, setImportStep]           = useState(1);
  const [importForm, setImportForm]           = useState({ concession: '', allee: '' });
  const [importRows, setImportRows]           = useState([]);
  const [importFilename, setImportFilename]   = useState('');
  const [importLoading, setImportLoading]     = useState(false);
  const [importResult, setImportResult]       = useState(null);
  const [importError, setImportError]         = useState('');
  const importFileRef = useRef(null);

  // Vue active : tableau ou carte
  const [parkingView, setParkingView] = useState('tableau');

  // Statuts parking (référentiel)
  const [statuts, setStatuts] = useState([]);
  const [responsables, setResponsables] = useState([]);

  // Sélection groupée
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkStatutModal, setShowBulkStatutModal] = useState(false);
  const [bulkStatut, setBulkStatut] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);

  // Batch analyse
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchCount, setBatchCount] = useState(0);
  const batchStopRef = useRef(false);
  // Snapshot des entrées de navigation au moment de l'ouverture de la modale.
  // Permet de maintenir le compteur et la navigation prev/next stables même
  // si filteredEntries change (ex : changement de statut qui sort l'entrée du filtre actif).
  const navEntriesRef = useRef([]);

  // Modal édition véhicule
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm]           = useState({});
  const [editError, setEditError]         = useState('');
  // Conflit d'identifiant : la fiche cible proposée à la fusion. La fusion
  // n'est PLUS automatique — elle demande un geste explicite de l'opérateur.
  const [conflitFusion, setConflitFusion]  = useState(null);
  const [fusionEnCours, setFusionEnCours]  = useState(false);
  const [statusChanging, setStatusChanging] = useState(false);
  const [autoSaved, setAutoSaved]         = useState(false);
  const autoSaveInitRef                   = useRef(false);
  const autoSaveTimerRef                  = useRef(null);
  const autoSavePendingRef                = useRef(null);

  // Notes parking
  const [notes, setNotes]               = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [noteForm, setNoteForm]         = useState({ contenu: '', statut: 'info' });
  const [noteAdding, setNoteAdding]     = useState(false);

  // Facture Services liée à l'immat
  const [factureImmat, setFactureImmat]               = useState(null);
  const [factureImmatLoading, setFactureImmatLoading] = useState(false);
  const [showFactureDetail, setShowFactureDetail]     = useState(false);
  const [facturesBadgeMap, setFacturesBadgeMap]       = useState({});

  // Lightbox photo
  const [lightbox, setLightbox] = useState({ open: false, src: null, loading: false });
  const photoCache = useRef({});

  // Fiche véhicule — photo inline + OR/litige en cours + actions parking
  const [modalPhotoSrc, setModalPhotoSrc]         = useState(null);
  const [modalPhotoLoading, setModalPhotoLoading] = useState(false);
  const [orEnCoursDetail, setOrEnCoursDetail]     = useState(null);
  const [orEnCoursLoading, setOrEnCoursLoading]   = useState(false);
  const [litigeDetail, setLitigeDetail]           = useState(null);
  const [litigeLoading, setLitigeLoading]         = useState(false);
  const [proprietairesList, setProprietairesList] = useState([]);


  /* ── Chargement ── */
  const loadEntries = useCallback(async (showArchived) => {
    try {
      setLoading(true);
      const params = showArchived ? { archived: 'true' } : {};
      const res = await api.get('/parking', { params });
      // Page = inventaire de ce qu'on a réellement vu sur le parc : les positions
      // déclarées au comptoir depuis le Planning (`source = 'crm'`) n'y figurent
      // pas. ⚠️ `|| 'scan'` : les lignes antérieures à la colonne `source` sont
      // des relevés, sans quoi elles disparaîtraient toutes.
      const entriesList = res.data.entries.filter(e => (e.source || 'scan') === 'scan');
      setEntries(entriesList);
      // Chargement asynchrone des badges factures pour tous les immat
      const immatList = [...new Set(entriesList.map(e => e.identifiant).filter(Boolean))];
      if (immatList.length > 0) {
        api.post('/factures-services/by-immat-batch', { immat: immatList })
          .then(batchRes => setFacturesBadgeMap(batchRes.data.factures || {}))
          .catch(() => {});
      }
    } catch (error) {
      console.error('Erreur chargement parking:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fermer le menu impression au clic extérieur
  useEffect(() => {
    if (!showPrintMenu) return;
    const handler = (e) => {
      if (printMenuRef.current && !printMenuRef.current.contains(e.target)) setShowPrintMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showPrintMenu]);


  // Charger le détail commande quand on clique sur un badge Pièces
  useEffect(() => {
    if (!commandeDetailOr) { setCommandeDetailData(null); return; }
    setCommandeDetailLoading(true);
    carnetCommandeApi.getByOr(commandeDetailOr)
      .then(res => setCommandeDetailData(res.data))
      .catch(() => setCommandeDetailData(null))
      .finally(() => setCommandeDetailLoading(false));
  }, [commandeDetailOr]);

  // Recharger quand on bascule entre tableau et archives
  useEffect(() => {
    loadEntries(parkingView === 'archives');
  }, [parkingView]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // La liste n'est PAS chargée ici : l'effet sur parkingView (ci-dessus) s'en
    // charge déjà au montage — les deux ensemble lançaient deux fois la requête
    // la plus lourde du CRM à chaque ouverture de la page (constaté le 04/10).
    statutsParkingApi.getAll()
      .then(res => setStatuts(res.data.statuts || []))
      .catch(() => {});
    referentiels.getResponsables()
      .then(res => {
        const list = res.data.responsables || [];
        setResponsables(list);
        const storedId = localStorage.getItem('parking_filter_responsable');
        if (storedId) {
          const found = list.find(r => String(r.id) === storedId);
          // filter restored via filters.responsable from localStorage
        }
      })
      .catch(err => console.error('[parking] chargement responsables échoué:', err));
    if (!isOperateur) {
      api.get('/parking/concessions')
        .then(res => setConcessionsList(res.data.concessions || []))
        .catch(() => {});
    }
  }, [loadEntries]); // eslint-disable-line react-hooks/exhaustive-deps

  // Chargement des propriétaires au montage
  useEffect(() => {
    api.get('/parking/proprietaires')
      .then(r => setProprietairesList(r.data.proprietaires || []))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Lightbox photo ── */
  const openLightbox = (e, entryId) => {
    e.stopPropagation();
    if (photoCache.current[entryId] !== undefined) {
      setLightbox({ open: true, src: photoCache.current[entryId], loading: false });
      return;
    }
    setLightbox({ open: true, src: null, loading: true });
    api.get(`/parking/${entryId}/photo`)
      .then(res => {
        const data = res.data.image_data || null;
        photoCache.current[entryId] = data;
        setLightbox({ open: true, src: data, loading: false });
      })
      .catch(() => setLightbox({ open: true, src: null, loading: false }));
  };

  /* ── Ouverture modale au clic ligne ── */
  const handleRowClick = (entry, navOverride) => {
    navEntriesRef.current = navOverride || sortedEntries; // snapshot de la liste courante pour la navigation
    setSelectedEntry(entry);
    setEditForm({
      marque:       entry.marque || '',
      allee:        entry.allee || '',
      identifiant:  entry.identifiant || '',
      proprietaire: entry.proprietaire || '',
    });
    setEditError('');
    setNotes([]);
    setNoteForm({ contenu: '', statut: 'info' });
    setOrEnCoursDetail(null);
    setLitigeDetail(null);
    setModalPhotoSrc(null);
    autoSaveInitRef.current = false;
    setShowEditModal(true);
  };

  const cancelEdit = () => {
    flushAutoSave();
    setShowEditModal(false);
    setSelectedEntry(null);
    setEditError('');
    setConflitFusion(null);
    setNotes([]);
    setFactureImmat(null);
    setFactureImmatLoading(false);
    setShowFactureDetail(false);
    setOrEnCoursDetail(null);
    setLitigeDetail(null);
    setModalPhotoSrc(null);
  };

  /* ── Mise à jour statut inline ── */
  const handleUpdateStatutInline = async (id, newStatut) => {
    try {
      await api.put(`/parking/${id}/statut`, { statut: newStatut });
      setEntries(prev => prev.map(e => e.id === id ? { ...e, statut: newStatut } : e));
    } catch (err) {
      console.error('[parking] statut inline error:', err);
    }
  };

  /* ── Mise à jour responsable inline (optimiste) ── */
  const handleUpdateResponsable = async (id, responsable_id) => {
    const val = responsable_id ? parseInt(responsable_id, 10) : null;
    const prevEntries = entries;
    const prevSelected = selectedEntry;
    // Mise à jour optimiste : entries + selectedEntry ensemble pour éviter le flicker du <select> contrôlé
    setEntries(prev => prev.map(e => e.id === id ? { ...e, responsable_id: val } : e));
    if (selectedEntry?.id === id) {
      setSelectedEntry(prev => ({ ...prev, responsable_id: val }));
    }
    try {
      await api.patch(`/parking/${id}/responsable`, { responsable_id: val });
    } catch (err) {
      // Rollback en cas d'échec + message visible dans la modale si pertinent
      console.error('[parking] responsable inline error:', err);
      setEntries(prevEntries);
      if (prevSelected?.id === id) setSelectedEntry(prevSelected);
      setEditError(err.response?.data?.error || "Impossible d'enregistrer le responsable");
      throw err;
    }
  };

  /* ── Mise à jour nom_client inline ── */
  const handleSaveNomClient = async (id, val) => {
    setNomClientEdit(null);
    const trimmed = val.trim() || null;
    try {
      await api.patch(`/parking/${id}/nom-client`, { nom_client: trimmed });
      setEntries(prev => prev.map(e => e.id === id ? { ...e, nom_client: trimmed } : e));
    } catch (err) {
      console.error('[parking] nom_client inline error:', err);
    }
  };

  /* ── Auto-save des champs texte (debounce 800ms) ── */
  const doAutoSave = async (entryId, form) => {
    if (!entryId || !form.identifiant?.trim()) return;
    try {
      const res = await api.put(`/parking/${entryId}`, {
        marque:       form.marque || null,
        allee:        form.allee || null,
        identifiant:  form.identifiant.trim().toUpperCase(),
        statut:       null,
        proprietaire: form.proprietaire || null,
      });
      const updated = res.data.entry;
      if (res.data.merged) {
        // Fusion : l'entrée courante a été supprimée, on ferme la modale et on rafraîchit
        autoSavePendingRef.current = null;
        cancelEdit();
        loadEntries();
        return;
      }
      setSelectedEntry(prev => prev?.id === updated.id ? { ...prev, ...updated } : prev);
      setEntries(prev => prev.map(e => e.id === updated.id ? { ...e, ...updated } : e));
      if (form.proprietaire && !proprietairesList.includes(form.proprietaire)) {
        api.get('/parking/proprietaires').then(r => setProprietairesList(r.data.proprietaires || [])).catch(() => {});
      }
      autoSavePendingRef.current = null;
      setAutoSaved(true);
      setEditError('');
      setTimeout(() => setAutoSaved(false), 2000);
    } catch (error) {
      const d = error.response?.data || {};
      // 409 « identifiant déjà pris » : on PROPOSE la fusion, on ne la fait pas.
      // Avant, le serveur fusionnait seul et la fiche courante disparaissait
      // sans un mot — sur une erreur de lecture, un véhicule réel sortait de
      // l'inventaire sans laisser de trace.
      if (error.response?.status === 409 && d.fusion_possible && d.cible) {
        setConflitFusion(d.cible);
        setEditError('');
      } else {
        setEditError(d.error || 'Erreur lors de la sauvegarde');
      }
      console.error('Erreur auto-save parking:', d.error || error.message);
    }
  };

  // Fusion confirmée : la fiche courante est absorbée par la cible, et
  // l'opération est consignée (table parking_fusions) pour pouvoir la défaire.
  const confirmerFusion = async () => {
    if (!conflitFusion || !selectedEntry || fusionEnCours) return;
    setFusionEnCours(true);
    try {
      await api.post(`/parking/${selectedEntry.id}/fusion`, { cible_id: conflitFusion.id });
      setConflitFusion(null);
      autoSavePendingRef.current = null;
      cancelEdit();
      loadEntries();
    } catch (err) {
      setEditError(err.response?.data?.error || 'Fusion impossible');
      setConflitFusion(null);
    } finally {
      setFusionEnCours(false);
    }
  };

  const flushAutoSave = () => {
    clearTimeout(autoSaveTimerRef.current);
    if (autoSavePendingRef.current) {
      const { entryId, form } = autoSavePendingRef.current;
      autoSavePendingRef.current = null;
      doAutoSave(entryId, form);
    }
  };

  useEffect(() => {
    if (!selectedEntry?.id) return;
    if (!autoSaveInitRef.current) {
      autoSaveInitRef.current = true;
      return;
    }
    if (!editForm.identifiant?.trim()) return;

    autoSavePendingRef.current = { entryId: selectedEntry.id, form: { ...editForm } };
    clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      doAutoSave(selectedEntry.id, editForm);
    }, 800);

    return () => clearTimeout(autoSaveTimerRef.current);
  }, [editForm]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Navigation prev/next entre fiches ── */
  const handleNavEntry = (entry) => {
    flushAutoSave();
    setSelectedEntry(entry);
    autoSaveInitRef.current = false;
    setEditForm({
      marque:       entry.marque || '',
      allee:        entry.allee || '',
      identifiant:  entry.identifiant || '',
      proprietaire: entry.proprietaire || '',
    });
    setEditError('');
    setNotes([]);
    setNoteForm({ contenu: '', statut: 'info' });
    setShowFactureDetail(false);
    setOrEnCoursDetail(null);
    setLitigeDetail(null);
    setModalPhotoSrc(null);
  };

  /* ── Changement de statut rapide ── */
  const handleQuickStatutChange = async (newStatut) => {
    if (newStatut === selectedEntry.statut || statusChanging) return;
    try {
      setStatusChanging(true);
      const res = await api.put(`/parking/${selectedEntry.id}`, {
        marque:       editForm.marque || null,
        allee:        editForm.allee || null,
        identifiant:  editForm.identifiant?.trim().toUpperCase() || selectedEntry.identifiant,
        statut:       newStatut,
        proprietaire: editForm.proprietaire || null,
      });
      const updated = res.data.entry;
      setSelectedEntry(prev => ({ ...prev, ...updated }));
      setEntries(prev => prev.map(e => e.id === updated.id ? { ...e, ...updated } : e));
    } catch (error) {
      console.error('Erreur changement statut:', error);
    } finally {
      setStatusChanging(false);
    }
  };

  /* ── Photo inline + OR en cours + Litige ── */
  useEffect(() => {
    if (!selectedEntry?.id) return;
    // Photo inline dans la modale
    if (photoCache.current[selectedEntry.id] !== undefined) {
      setModalPhotoSrc(photoCache.current[selectedEntry.id]);
    } else {
      setModalPhotoLoading(true);
      api.get(`/parking/${selectedEntry.id}/photo`)
        .then(res => {
          const data = res.data.image_data || null;
          photoCache.current[selectedEntry.id] = data;
          setModalPhotoSrc(data);
        })
        .catch(() => setModalPhotoSrc(null))
        .finally(() => setModalPhotoLoading(false));
    }
    // OR en cours
    if (selectedEntry.or_en_cours_id) {
      setOrEnCoursLoading(true);
      api.get(`/or-en-cours/${selectedEntry.or_en_cours_id}`)
        .then(r => setOrEnCoursDetail(r.data))
        .catch(() => {})
        .finally(() => setOrEnCoursLoading(false));
    }
    // Litige en cours
    if (selectedEntry.litige_id) {
      setLitigeLoading(true);
      litigesApi.getById(selectedEntry.litige_id)
        .then(r => setLitigeDetail(r.data))
        .catch(() => {})
        .finally(() => setLitigeLoading(false));
    }
  }, [selectedEntry?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Notes parking ── */
  useEffect(() => {
    if (!selectedEntry?.id) return;
    setNotesLoading(true);
    api.get(`/parking/${selectedEntry.id}/notes`)
      .then(res => setNotes(res.data.notes || []))
      .catch(() => setNotes([]))
      .finally(() => setNotesLoading(false));
  }, [selectedEntry?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Dernière facture atelier pour l'immat sélectionné ── */
  useEffect(() => {
    if (!selectedEntry?.identifiant) { setFactureImmat(null); return; }
    setFactureImmatLoading(true);
    setFactureImmat(null);
    api.get(`/factures-services/by-immat/${selectedEntry.identifiant}`)
      .then(res => setFactureImmat(res.data.derniere || null))
      .catch(() => setFactureImmat(null))
      .finally(() => setFactureImmatLoading(false));
  }, [selectedEntry?.identifiant]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Persistance des filtres ── */
  useEffect(() => {
    localStorage.setItem(FILTERS_KEY, JSON.stringify(filters));
  }, [filters]);

  const handleAddNote = async () => {
    if (!noteForm.contenu.trim() || noteAdding) return;
    try {
      setNoteAdding(true);
      const res = await api.post(`/parking/${selectedEntry.id}/notes`, {
        contenu: noteForm.contenu.trim(),
        statut:  noteForm.statut,
      });
      setNotes(prev => [res.data.note, ...prev]);
      setNoteForm({ contenu: '', statut: 'info' });
      // Met à jour notes_count dans le tableau
      setEntries(prev => prev.map(e =>
        e.id === selectedEntry.id ? { ...e, notes_count: (e.notes_count || 0) + 1 } : e
      ));
    } catch (error) {
      console.error('Erreur ajout note:', error);
    } finally {
      setNoteAdding(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await api.delete(`/parking/${selectedEntry.id}/notes/${noteId}`);
      setNotes(prev => prev.filter(n => n.id !== noteId));
      setEntries(prev => prev.map(e =>
        e.id === selectedEntry.id ? { ...e, notes_count: Math.max(0, (e.notes_count || 1) - 1) } : e
      ));
    } catch (error) {
      console.error('Erreur suppression note:', error);
    }
  };

  const NOTE_STATUTS = [
    { value: 'info',   label: 'Info',    color: 'var(--color-info)', bg: 'light-dark(#e0f2fe, var(--color-info-light))' },
    { value: 'a_faire', label: 'À faire', color: 'var(--color-warning)', bg: 'light-dark(#fef3c7, var(--color-warning-light))' },
    { value: 'urgent', label: 'Urgent',  color: 'var(--color-error)', bg: 'light-dark(#fee2e2, var(--color-error-light))' },
  ];
  const getNoteStatut = (val) => NOTE_STATUTS.find(s => s.value === val) || NOTE_STATUTS[0];

  const formatNoteDate = (s) => {
    if (!s) return '';
    return new Date(s).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  /* ── Ajout manuel ── */
  const openAddModal  = () => { setFormData(EMPTY_FORM); setFormError(''); setShowAddModal(true); };
  const closeAddModal = () => { setShowAddModal(false); setFormError(''); };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!formData.identifiant.trim()) { setFormError('L\'identifiant est obligatoire.'); return; }
    try {
      setFormSaving(true);
      const res = await api.post('/parking', {
        agent:            formData.agent || null,
        allee:            formData.allee || null,
        identifiant:      formData.identifiant.trim(),
        type_identifiant: formData.type_identifiant,
        marque:           formData.marque || null,
        confidence:       formData.confidence !== '' ? parseFloat(formData.confidence) : null,
      });
      setEntries(prev => [res.data.entry, ...prev]);
      closeAddModal();
    } catch (error) {
      setFormError(error.response?.data?.error || 'Erreur lors de la création.');
    } finally {
      setFormSaving(false);
    }
  };

  /* ── Import Excel ── */
  const openImportModal = () => {
    setImportStep(1);
    setImportForm({ concession: '', allee: '' });
    setImportRows([]);
    setImportFilename('');
    setImportError('');
    setImportResult(null);
    setShowImportModal(true);
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    if (importFileRef.current) importFileRef.current.value = '';
  };

  const detectImportColumns = (headers) => {
    const h = headers.map(col => (col || '').toString().toLowerCase());
    const find = (...keywords) => {
      const idx = h.findIndex(col => keywords.some(k => col.includes(k)));
      return idx;
    };
    return {
      identifiant: find('plaque', 'immat', 'immatriculation', 'identifiant', 'vehicule', 'véhicule'),
      marque: find('marque', 'brand'),
    };
  };

  const handleImportFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const wb = XLSX.read(data, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const jsonRows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        if (jsonRows.length < 2) {
          setImportError('Fichier vide ou sans données.');
          return;
        }
        const headers = jsonRows[0].map(h => (h || '').toString());
        const colMap = detectImportColumns(headers);
        if (colMap.identifiant < 0) {
          setImportError('Colonne identifiant/plaque/immatriculation introuvable dans le fichier.');
          return;
        }
        const rows = jsonRows.slice(1).map(row => ({
          identifiant: colMap.identifiant >= 0 ? (row[colMap.identifiant] || '').toString().trim().toUpperCase() : '',
          marque:      colMap.marque >= 0      ? (row[colMap.marque]      || '').toString().trim() : '',
        })).filter(r => r.identifiant);
        if (rows.length === 0) {
          setImportError('Aucun véhicule trouvé dans le fichier.');
          return;
        }
        setImportError('');
        setImportRows(rows);
        setImportFilename(file.name);
      } catch {
        setImportError('Erreur de lecture du fichier.');
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const downloadParkingTemplate = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([
      ['identifiant', 'marque', 'concession', 'allee'],
      ['AB123CD', 'Peugeot', 'CENTRAUTODIS', 'Parking Client'],
      ['EF456GH', 'Renault', 'CENTRAUTODIS', 'Parking Client'],
    ]);
    XLSX.utils.book_append_sheet(wb, ws, 'Modèle');
    XLSX.writeFile(wb, 'modele_parking.xlsx');
  };

  const handleImportConfirm = async () => {
    try {
      setImportLoading(true);
      setImportError('');
      const res = await api.post('/parking/import-excel', {
        concession: importForm.concession,
        allee:      importForm.allee,
        rows:       importRows,
      });
      setImportResult(res.data);
      setImportStep(3);
      await loadEntries();
    } catch (error) {
      setImportError(error.response?.data?.error || 'Erreur lors de l\'import.');
    } finally {
      setImportLoading(false);
    }
  };

  /* ── Suppression ── */
  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Supprimer définitivement cette entrée ?')) return;
    try {
      await api.delete(`/parking/${id}`);
      setEntries(prev => prev.filter(e => e.id !== id));
      if (selectedEntry?.id === id) cancelEdit();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur lors de la suppression.');
    }
  };

  /* ── Sélection groupée ── */
  const toggleSelectEntry = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Supprimer définitivement ${selectedIds.size} véhicule(s) ? Cette action est irréversible.`)) return;
    setBulkLoading(true);
    try {
      await api.delete('/parking/bulk', { data: { ids: [...selectedIds] } });
      setEntries(prev => prev.filter(e => !selectedIds.has(e.id)));
      setSelectedIds(new Set());
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la suppression groupée');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleArchive = async (id, archive = true) => {
    try {
      await api.patch(`/parking/${id}/archive`, { archived: archive });
      setEntries(prev => prev.filter(e => e.id !== id));
      if (selectedEntry?.id === id) cancelEdit();
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur');
    }
  };

  const handleBulkArchive = async () => {
    const action = parkingView === 'archives' ? 'désarchiver' : 'archiver';
    if (!window.confirm(`${parkingView === 'archives' ? 'Désarchiver' : 'Archiver'} ${selectedIds.size} véhicule(s) ?`)) return;
    setBulkLoading(true);
    try {
      await api.put('/parking/bulk-archive', { ids: [...selectedIds], archived: parkingView !== 'archives' });
      setEntries(prev => prev.filter(e => !selectedIds.has(e.id)));
      setSelectedIds(new Set());
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkEditStatut = async () => {
    if (!bulkStatut) return;
    setBulkLoading(true);
    try {
      await api.put('/parking/bulk', { ids: [...selectedIds], statut: bulkStatut });
      const sp = statuts.find(s => s.valeur === bulkStatut);
      setEntries(prev => prev.map(e =>
        selectedIds.has(e.id)
          ? { ...e, statut: bulkStatut, statut_nom: sp?.nom, statut_couleur: sp?.couleur }
          : e
      ));
      setSelectedIds(new Set());
      setShowBulkStatutModal(false);
      setBulkStatut('');
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la modification groupée');
    } finally {
      setBulkLoading(false);
    }
  };

  /* ── Batch analyse inventaire ── */
  const runBatchAnalysis = async () => {
    if (batchRunning) {
      batchStopRef.current = true;
      return;
    }
    batchStopRef.current = false;
    setBatchRunning(true);
    setBatchCount(0);
    let total = 0;
    try {
      while (!batchStopRef.current) {
        const res = await api.post('/parking/trigger-batch');
        const analyzed = res.data?.analyzed ?? 0;
        total += analyzed;
        setBatchCount(total);
        if (analyzed === 0) break;
      }
    } catch (err) {
      console.error('Erreur batch analyse:', err);
    } finally {
      setBatchRunning(false);
      batchStopRef.current = false;
      if (total > 0) loadEntries();
    }
  };

  /* ── Filtres ── */
  const clearFilters = () => {
    localStorage.removeItem(FILTERS_KEY);
    localStorage.removeItem('parking_filter_responsable');
    setFilters({
      search: '', plaque: '', concession: '', zones: [], statut: '', statutAtelier: '', pieces: '',
      ancienneteActu: '', responsable: '',
      alertes: { vo: false, litige: false, lead: false }
    });
  };

  const toggleAlerte = (key) =>
    setFilters(f => ({ ...f, alertes: { ...f.alertes, [key]: !f.alertes[key] } }));

  const activeFiltersCount = [
    filters.concession, filters.plaque, filters.zones.length > 0, filters.statut, filters.statutAtelier, filters.pieces,
    filters.ancienneteActu, filters.responsable,
    filters.alertes.vo, filters.alertes.litige, filters.alertes.lead,
  ].filter(Boolean).length;

  // Statuts atelier présents dans les lignes AFFICHABLES : la liste du filtre
  // suit les données (référentiel global, mais seuls comptent ceux du parc).
  const statutsAtelierPresents = [...new Set(entries.map(e => e.planning_statut_nom).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, 'fr'));

  /* ── Tri ── */
  const handleSort = (col) =>
    setSort(s => s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' });

  const sortIcon = (col) => (
    <span className={`sort-icon ${sort.col === col ? 'active' : ''}`}>
      {sort.col === col ? (sort.dir === 'asc' ? '▲' : '▼') : '⇅'}
    </span>
  );

  /* ── Actions rapides sur un véhicule du parking (bouton +) ────────────────
     Le « + » ne créait qu'un dossier Réception VIP, sans le dire. Il ouvre
     désormais un choix d'actions ; la création d'intervention atelier se fait
     SANS quitter le parking, en réutilisant la modale du Planning. */
  const [quickActionEntry, setQuickActionEntry] = useState(null);   // entrée ciblée par le +
  const [showInterventionModal, setShowInterventionModal] = useState(false);
  // Référentiels du Planning, chargés une seule fois et à la demande (le parking
  // n'en a pas besoin autrement — pas question de les charger à chaque affichage).
  const [planningRefs, setPlanningRefs] = useState(null);
  const [planningRefsLoading, setPlanningRefsLoading] = useState(false);

  const loadPlanningRefs = useCallback(async () => {
    if (planningRefs || planningRefsLoading) return planningRefs;
    setPlanningRefsLoading(true);
    try {
      const [statuts, travaux, staff] = await Promise.all([
        api.get('/referentiels/statuts-atelier'),
        api.get('/referentiels/travaux-types'),
        api.get('/planning/staff'),
      ]);
      const list = staff.data || [];
      const refs = {
        statutsAtelier: statuts.data?.statuts_atelier || [],
        travauxTypes: travaux.data?.travaux_types || [],
        mecaniciens: list.filter(s => s.role === 'mecanicien' && s.actif !== false),
        receptionnaires: list.filter(s => s.role === 'receptionnaire' && s.actif !== false),
      };
      setPlanningRefs(refs);
      return refs;
    } catch (err) {
      console.error('[parking/planning-refs]', err);
      alert("Impossible de charger les référentiels du Planning Atelier.");
      return null;
    } finally {
      setPlanningRefsLoading(false);
    }
  }, [planningRefs, planningRefsLoading]);

  const openInterventionFromParking = async (entry) => {
    const refs = await loadPlanningRefs();
    if (!refs) return;
    setQuickActionEntry(entry);
    setShowInterventionModal(true);
  };

  /* ── Création rapide dossier Réception VIP ── */
  const handleCreateVip = async (entry) => {
    const concession = concessionsList.find(c => c.nom === entry.concession);
    try {
      await api.post('/reception-vip/manual', {
        client_username: entry.nom_client || entry.identifiant,
        plaque: entry.identifiant,
        description: entry.nom_client ? `Société : ${entry.nom_client} — Concession : ${entry.concession || '—'}` : null,
        concession_id: concession?.id || null,
      });
      alert('Dossier Réception VIP créé');
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur création dossier VIP');
    }
  };

  /* ── Données filtrées + triées ── */
  const filteredEntries = entries.filter(e => {
    if (filters.search) {
      const q = filters.search.toLowerCase();
      const qNorm = q.replace(/[-\s]/g, '');
      const identNorm = (e.identifiant || '').toLowerCase().replace(/[-\s]/g, '');
      if (!(
        identNorm.includes(qNorm) ||
        e.marque?.toLowerCase().includes(q) ||
        e.nom_client?.toLowerCase().includes(q) ||
        e.allee?.toLowerCase().includes(q) ||
        e.concession?.toLowerCase().includes(q) ||
        e.agent?.toLowerCase().includes(q)
      )) return false;
    }
    if (filters.plaque) {
      const pq = filters.plaque.replace(/[-\s]/g, '');
      const identNorm = (e.identifiant || '').toUpperCase().replace(/[-\s]/g, '');
      if (!identNorm.includes(pq)) return false;
    }
    // Statut du Planning Atelier (colonne Statut atelier). Filtre par NOM :
    // les statuts n'ont pas d'id côté parking, et c'est le nom qu'on lit.
    if (filters.statutAtelier) {
      if (filters.statutAtelier === 'aucun') {
        if (e.planning_statut_nom) return false;
      } else {
        if (e.planning_statut_nom !== filters.statutAtelier) return false;
      }
    }
    // Pièces : même lecture que la colonne (livre → Reçu, recu_partiellement →
    // Partiel, toute autre valeur → Commandé, vide → sans commande).
    if (filters.pieces) {
      const sc = e.statut_commande;
      if (filters.pieces === 'aucune' && sc) return false;
      if (filters.pieces === 'livre' && sc !== 'livre') return false;
      if (filters.pieces === 'recu_partiellement' && sc !== 'recu_partiellement') return false;
      if (filters.pieces === 'en_commande' && (!sc || sc === 'livre' || sc === 'recu_partiellement')) return false;
    }
    if (filters.concession  && e.concession !== filters.concession)  return false;
    if (!filters.concession && user?.concession_nom && e.concession !== user.concession_nom) return false;
    if (filters.zones.length > 0 && !filters.zones.includes(e.allee)) return false;
    if (filters.statut      && e.statut     !== filters.statut)      return false;
    if (filters.ancienneteActu && getAncienneteActu(e).value !== filters.ancienneteActu) return false;
    if (filters.responsable && String(e.responsable_id || '') !== filters.responsable) return false;
    const { vo, litige, lead } = filters.alertes;
    if ((vo || litige || lead) && !((vo && e.has_vo) || (litige && e.litige_id) || (lead && e.has_lead))) return false;
    return true;
  });

  const DATE_COLS = new Set(['created_at', 'updated_at', 'derniere_facture_date', 'or_date_creation', 'planning_statut_since']);

  const sortedEntries = [...filteredEntries].sort((a, b) => {
    if (!sort.col) return 0;
    // Statut atelier : les véhicules SANS statut restent en queue dans les
    // deux sens — sans quoi le tri ascendant ouvrait sur un mur de « — »
    // (1 174 lignes mesurées) avant la première pastille datée.
    if (sort.col === 'planning_statut_since') {
      const aVide = !a.planning_statut_since, bVide = !b.planning_statut_since;
      if (aVide || bVide) return aVide && bVide ? 0 : (aVide ? 1 : -1);
    }
    // Planification : seules les dates à venir sont affichées ; les autres
    // (passées ou absentes) restent en queue dans les deux sens.
    if (sort.col === 'date_planification') {
      const pa = planificationAVenir(a), pb = planificationAVenir(b);
      if (!pa || !pb) return !pa && !pb ? 0 : (!pa ? 1 : -1);
      const c = pa < pb ? -1 : pa > pb ? 1 : 0;
      return sort.dir === 'asc' ? c : -c;
    }
    let va = DATE_COLS.has(sort.col) ? new Date(a[sort.col] || 0) : (a[sort.col] ?? '');
    let vb = DATE_COLS.has(sort.col) ? new Date(b[sort.col] || 0) : (b[sort.col] ?? '');
    if (typeof va === 'string') va = va.toLowerCase();
    if (typeof vb === 'string') vb = vb.toLowerCase();
    const cmp = va < vb ? -1 : va > vb ? 1 : 0;
    return sort.dir === 'asc' ? cmp : -cmp;
  });

  const formatDate = (s) => {
    if (!s) return '-';
    return new Date(s).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const exportValueOf = (e, key) => {
    switch (key) {
      case 'identifiant':     return e.identifiant  || '';
      case 'marque':          return e.marque       || '';
      case 'allee':           return e.allee        || '';
      case 'statut':          return e.statut       || '';
      case 'concession':      return e.concession   || '';
      case 'agent':           return e.agent        || '';
      case 'proprietaire':    return e.proprietaire || '';
      case 'nom_client':      return e.nom_client   || '';
      case 'created_at':      return formatDate(e.created_at);
      case 'updated_at':      return formatDate(e.updated_at);
      case 'statut_commande': return e.statut_commande || '';
      // Statut de la dernière intervention planifiée du véhicule (colonne
      // « Statut atelier »), et date de passage dans ce statut.
      case 'planning_statut': return e.planning_statut_nom || '';
      case 'planning_statut_since': return e.planning_statut_nom && e.planning_statut_since
        ? `${formatDate(e.planning_statut_since)}${e.planning_statut_since_estime ? ' (approx.)' : ''}` : '';
      case 'has_vo':          return e.has_vo    ? 'Oui' : '';
      case 'litige':          return e.litige_id ? 'Oui' : '';
      case 'lead':            return e.has_lead  ? 'Oui' : '';
      case 'or_en_cours':     return e.or_en_cours_id ? 'Oui' : '';
      case 'num_or':          return e.num_or || '';
      case 'or_date_creation': return e.or_date_creation ? formatDate(e.or_date_creation) : '';
      case 'notes':           return e.notes_count > 0 ? String(e.notes_count) : '';
      case 'latitude':        return e.latitude  != null ? e.latitude  : '';
      case 'longitude':       return e.longitude != null ? e.longitude : '';
      default:                return '';
    }
  };

  const toggleExportField = (key) => {
    setExportFields(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(EXPORT_FIELDS_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const setAllExportFields = (val) => {
    const next = {};
    EXPORT_COLUMN_DEFS.forEach(([k]) => { next[k] = val; });
    try { localStorage.setItem(EXPORT_FIELDS_KEY, JSON.stringify(next)); } catch {}
    setExportFields(next);
  };

  const exportSelectedCount = EXPORT_COLUMN_DEFS.filter(([k]) => exportFields[k]).length;

  const handleExportExcel = () => {
    const cols = EXPORT_COLUMN_DEFS.filter(([k]) => exportFields[k]);
    if (cols.length === 0) { alert('Sélectionnez au moins un champ à exporter.'); return; }
    const rows = sortedEntries.map(e => {
      const row = {};
      cols.forEach(([k, label]) => { row[label] = exportValueOf(e, k); });
      return row;
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Parking');
    const date = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `parking_${date}.xlsx`);
    setShowExportModal(false);
  };


  // Zones disponibles : celles des véhicules de l'onglet affiché (parc ou
  // archives), réduites à la concession filtrée.
  const zonesDispos = [...new Set(
    entries
      .filter(e => !filters.concession || e.concession === filters.concession)
      .map(e => e.allee)
      .filter(Boolean)
  )].sort();

  // Allées disponibles pour la concession sélectionnée dans l'import
  const alleesDispos = importForm.concession
    ? [...new Set(entries.filter(e => e.concession === importForm.concession).map(e => e.allee).filter(Boolean))].sort()
    : [];

  // 2026-08-15 : +1 colonne Statut atelier, −1 colonne Takata (supprimée du
  // tableau — le champ has_takata reste servi, l'onglet Rapport le consomme).
  const colCount = (isOperateur ? 7 : 8) + (isSuperAdmin ? 1 : 0);
  const allSelected = selectedIds.size > 0 && selectedIds.size === filteredEntries.length;

  /* ── Rendu ── */
  return (
    <div className="page-container">

      {/* ── Onglets Parking / Carte ── */}
      <div className="page-tabs">
        <button
          className={parkingView === 'tableau' ? 'active' : ''}
          onClick={() => setParkingView('tableau')}
        >
          📋 Parking
        </button>
        <button
          className={parkingView === 'carte' ? 'active' : ''}
          onClick={() => setParkingView('carte')}
        >
          🗺️ Carte
        </button>
        <button
          className={parkingView === 'rapport' ? 'active' : ''}
          onClick={() => setParkingView('rapport')}
        >
          📊 Rapport
        </button>
        <button
          className={parkingView === 'archives' ? 'active' : ''}
          onClick={() => setParkingView('archives')}
        >
          🗄️ Archives
        </button>
      </div>

      {/* ── Barre de filtres (FiltresBarre, même modèle qu'OR en cours, 2026-10-23) ──
          Recherche + bouton « Filtres » ; chaque filtre actif en pastille.
          Les véhicules sont tous chargés : les filtres portent sur tout le parc. */}
      {(() => {
        const maj = (patch) => setFilters(f => ({ ...f, ...patch }));
        const nomResp = (id) => { const r = responsables.find(x => String(x.id) === String(id)); return r ? `${r.nom}${r.prenom ? ' ' + r.prenom : ''}` : id; };
        const nomStatut = (v) => (statuts.find(x => x.valeur === v) || {}).nom || v;
        const libReleve = (v) => (ACTUALISATION_TRANCHES.find(t => t.value === v) || {}).label || v;
        const actifs = [
          filters.concession && { cle: 'concession', libelle: 'Concession', valeur: filters.concession, retirer: () => maj({ concession: '', zones: [] }) },
          filters.zones.length > 0 && { cle: 'zones', libelle: filters.zones.length > 1 ? 'Zones' : 'Zone', valeur: filters.zones.join(', '), retirer: () => maj({ zones: [] }) },
          filters.statutAtelier && { cle: 'statutAtelier', libelle: 'Statut atelier', valeur: filters.statutAtelier === 'aucun' ? 'Sans statut' : filters.statutAtelier, retirer: () => maj({ statutAtelier: '' }) },
          filters.pieces && { cle: 'pieces', libelle: 'Pièces', valeur: (PIECES_FILTRE.find(x => x.value === filters.pieces) || {}).label || filters.pieces, retirer: () => maj({ pieces: '' }) },
          filters.statut && { cle: 'statut', libelle: 'Statut', valeur: nomStatut(filters.statut), retirer: () => maj({ statut: '' }) },
          filters.responsable && { cle: 'responsable', libelle: 'Responsable', valeur: nomResp(filters.responsable), retirer: () => maj({ responsable: '' }) },
          filters.ancienneteActu && { cle: 'releve', libelle: 'Dernier relevé', valeur: libReleve(filters.ancienneteActu), retirer: () => maj({ ancienneteActu: '' }) },
          filters.alertes.vo && { cle: 'vo', libelle: '', valeur: 'Stock VO', retirer: () => toggleAlerte('vo') },
          filters.alertes.litige && { cle: 'litige', libelle: '', valeur: 'Avec litige', retirer: () => toggleAlerte('litige') },
          filters.alertes.lead && { cle: 'lead', libelle: '', valeur: 'Avec lead', retirer: () => toggleAlerte('lead') },
        ].filter(Boolean);
        const resultat = `${filteredEntries.length.toLocaleString('fr-FR')} véhicule${filteredEntries.length !== 1 ? 's' : ''}`;
        return (
          <div className="page-toolbar parking-toolbar">
            <div className="fb-barre">
              <input
                type="text"
                className="page-search fb-recherche"
                placeholder="🔍 Plaque, client, marque, zone, agent…"
                value={filters.search}
                onChange={e => setFilters(f => ({ ...f, search: e.target.value.replace(/-/g, '') }))}
                onPaste={e => {
                  e.preventDefault();
                  const text = e.clipboardData.getData('text/plain');
                  setFilters(f => ({ ...f, search: text.replace(/[-\s]/g, '') }));
                }}
              />
              {/* Plaque : champ dédié (rétabli le 2026-10-28). Ne cherche QUE dans
                  l'identifiant scanné, sans tiret ni espace : c'est ce qui permet
                  de retrouver les plaques mal lues par la webapp. */}
              <input
                type="text"
                className={`page-search${filters.plaque ? ' active-filter' : ''}`}
                style={{ width: 160 }}
                placeholder="🚗 Plaque..."
                value={filters.plaque}
                onChange={e => setFilters(f => ({ ...f, plaque: e.target.value.toUpperCase().replace(/[-\s]/g, '') }))}
              />
              {/* Dernier relevé : filtre dédié, comme la Période d'OR en cours. */}
              <BoutonPanneau libelle={filters.ancienneteActu ? `Relevé : ${libReleve(filters.ancienneteActu)}` : 'Dernier relevé'} icone={IconeCalendrier} aligne="gauche" largeur={320}>
                <Rubrique titre="Dernier passage de la webapp de scan">
                  <ChoixPastilles valeur={filters.ancienneteActu} onChange={v => maj({ ancienneteActu: v })} options={ACTUALISATION_TRANCHES} />
                </Rubrique>
              </BoutonPanneau>
              <BoutonPanneau libelle="Filtres" icone={IconeFiltres} compte={activeFiltersCount - (filters.ancienneteActu ? 1 : 0) - (filters.plaque ? 1 : 0)} largeur={380}>
                {/* Concession : choix du menu (2026-10-26), plus de liste propre à la page. */}
                {zonesDispos.length > 0 && (
                  <Rubrique titre="Zones">
                    <ChoixMultiples valeurs={filters.zones} onChange={v => maj({ zones: v })} options={zonesDispos.map(z => ({ value: z, label: z }))} />
                  </Rubrique>
                )}
                <Rubrique titre="Statut atelier du dossier">
                  <select value={filters.statutAtelier || ''} onChange={e => maj({ statutAtelier: e.target.value })}>
                    <option value="">Tous</option>
                    <option value="aucun">Sans statut</option>
                    {statutsAtelierPresents.map(nom => <option key={nom} value={nom}>{nom}</option>)}
                  </select>
                </Rubrique>
                <Rubrique titre="Pièces">
                  <ChoixPastilles valeur={filters.pieces} onChange={v => maj({ pieces: v })} options={PIECES_FILTRE} />
                </Rubrique>
                <Rubrique titre="Statut parking">
                  <select value={filters.statut} onChange={e => maj({ statut: e.target.value })}>
                    <option value="">Tous</option>
                    {statuts.map(st => <option key={st.valeur} value={st.valeur}>{st.nom}</option>)}
                  </select>
                </Rubrique>
                <Rubrique titre="Responsable">
                  <select value={filters.responsable} onChange={e => maj({ responsable: e.target.value })}>
                    <option value="">Tous</option>
                    {[...responsables].sort((a, b) => a.nom.localeCompare(b.nom)).map(r => (
                      <option key={r.id} value={String(r.id)}>{r.nom}{r.prenom ? ' ' + r.prenom : ''}</option>
                    ))}
                  </select>
                </Rubrique>
                <Interrupteur id="pk-vo" checked={filters.alertes.vo} onChange={() => toggleAlerte('vo')} libelle="Stock VO" aide="Le véhicule est dans le stock VO" />
                <Interrupteur id="pk-litige" checked={filters.alertes.litige} onChange={() => toggleAlerte('litige')} libelle="⚖️ Avec litige" aide="Un dossier existe dans l'onglet Litiges" />
                <Interrupteur id="pk-lead" checked={filters.alertes.lead} onChange={() => toggleAlerte('lead')} libelle="Avec lead" aide="Un lead est en cours pour ce véhicule" />
                <div className="fb-pied"><span>{resultat}</span><button type="button" className="fb-lien" onClick={clearFilters}>Tout effacer</button></div>
              </BoutonPanneau>
            </div>
            <Pastilles filtres={actifs} onToutEffacer={clearFilters} resultat={resultat} />
          </div>
        );
      })()}

      {/* ── Vue Tableau uniquement ── */}
      {(parkingView === 'tableau' || parkingView === 'archives') && <>

      {/* ── Barre actions groupées ── */}
      {editMode && canManageBulk && selectedIds.size > 0 && (
        <div className="bulk-action-bar">
          <span className="bulk-count">{selectedIds.size} véhicule(s) sélectionné(s)</span>
          <button className="bulk-btn bulk-btn-edit" onClick={() => { setBulkStatut(''); setShowBulkStatutModal(true); }}>
            ✏️ Modifier le statut
          </button>
          <button className="bulk-btn" style={{ background: 'var(--color-info-light)', color: 'var(--color-info-dark)', border: '1px solid light-dark(#bfdbfe, var(--border-strong))' }} onClick={handleBulkArchive} disabled={bulkLoading}>
            {parkingView === 'archives' ? '📋 Désarchiver' : '🗄️ Archiver'}
          </button>
          <button className="bulk-btn bulk-btn-delete" onClick={handleBulkDelete} disabled={bulkLoading}>
            🗑 Supprimer
          </button>
          <button className="bulk-btn bulk-btn-cancel" onClick={() => setSelectedIds(new Set())}>
            ✕ Désélectionner tout
          </button>
        </div>
      )}

      {/* ── Corps : tableau ── */}
      <div className="parking-body">

        {/* Tableau */}
        <div className="parking-table-area">
          {loading ? (
            <div className="parking-loading">Chargement...</div>
          ) : (
            <div className="table-container">
              <table className="parking-table">
                <thead>
                  <tr>
                    {editMode && canManageBulk && (
                      <th className="col-checkbox" onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={() => allSelected ? setSelectedIds(new Set()) : setSelectedIds(new Set(filteredEntries.map(e => e.id)))}
                          title="Tout sélectionner"
                        />
                      </th>
                    )}
                    <th className="sortable col-date" onClick={() => handleSort('updated_at')}>
                      Détection {sortIcon('updated_at')}
                    </th>
                    <th className="sortable col-vehicule" onClick={() => handleSort('identifiant')}>
                      Véhicule {sortIcon('identifiant')}
                    </th>
                    <th className="sortable col-marque" onClick={() => handleSort('marque')}>
                      Marque {sortIcon('marque')}
                    </th>
                    <th className="sortable col-nom-client" onClick={() => handleSort('nom_client')}>
                      Client {sortIcon('nom_client')}
                    </th>
                    <th className="sortable" onClick={() => handleSort('or_date_creation')}>
                      OR en cours {sortIcon('or_date_creation')}
                    </th>
                    <th className="sortable" onClick={() => handleSort('statut_commande')}>
                      Pièces {sortIcon('statut_commande')}
                    </th>
                    <th className="sortable col-facture" onClick={() => handleSort('derniere_facture_date')}>
                      Facture {sortIcon('derniere_facture_date')}
                    </th>
                    <th className="sortable" style={{ whiteSpace: 'nowrap', width: '1%' }}
                      title="Statut du dossier au Planning Atelier — le tri porte sur la date de passage dans le statut"
                      onClick={() => handleSort('planning_statut_since')}>
                      Statut atelier {sortIcon('planning_statut_since')}
                    </th>
                    <th className="sortable" style={{ whiteSpace: 'nowrap', width: '1%' }} onClick={() => handleSort('date_planification')}>Planification {sortIcon('date_planification')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedEntries.length === 0 ? (
                    <tr>
                      <td colSpan={colCount} className="parking-empty-row">
                        {entries.length === 0
                          ? 'Aucune entrée. Utilisez "Nouvelle entrée" pour en ajouter une manuellement, ou lancez une analyse depuis l\'inventaire.'
                          : 'Aucun résultat pour cette recherche.'}
                      </td>
                    </tr>
                  ) : sortedEntries.map(entry => {
                    const anc = getAnciennete(entry.created_at);
                    const sp = statuts.find(s => s.valeur === entry.statut);
                    const statutColor = sp?.couleur || '#94a3b8';
                    return (
                      <tr
                        key={entry.id}
                        className={`parking-row ${selectedEntry?.id === entry.id ? 'row-selected' : ''}${selectedIds.has(entry.id) ? ' row-bulk-selected' : ''}`}
                        style={{  }}
                        onClick={() => handleRowClick(entry)}
                      >
                        {editMode && canManageBulk && (
                          <td className="col-checkbox" onClick={e => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedIds.has(entry.id)}
                              onChange={() => toggleSelectEntry(entry.id)}
                            />
                          </td>
                        )}
                        <td>
                          {entry.updated_at && new Date(entry.updated_at) > new Date(entry.created_at) ? (
                            <div className="date-cell">
                              <span className="date-text">{formatDate(entry.updated_at)}</span>
                              <span className="date-detection">Dét. {formatDate(entry.created_at)}</span>
                            </div>
                          ) : (
                            <div className="date-cell">
                              <span className="date-text">{formatDate(entry.created_at)}</span>
                            </div>
                          )}
                        </td>
                        <td className="col-vehicule">
                          <div className="vehicle-cell">
                            {entry.identifiant
                              ? <span
                                  className="plaque-badge plaque-badge-link pk-ident"
                                  onClick={e => { e.stopPropagation(); onOpenVehicule(entry.identifiant); }}
                                  title={`${entry.identifiant} — voir la fiche véhicule`}
                                >{entry.identifiant}</span>
                              : '-'}
                          </div>
                        </td>
                        <td className="col-marque" style={{ fontSize: 12, whiteSpace: 'nowrap', textTransform: 'uppercase' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span>{entry.marque && entry.marque.toUpperCase() !== 'UNKNOWN' ? entry.marque : null}</span>
                            {entry.notes_count > 0 && (
                              <span className="parking-notes-picto" title={`${entry.notes_count} note(s)`}>
                                💬 {entry.notes_count}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="col-nom-client">
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={entry.nom_client || undefined}>{entry.nom_client || null}</span>
                            {/* Visible de TOUS depuis le 2026-08-15 (décision
                                utilisateur) — les écritures restent gardées côté
                                serveur (permissions planning / réception VIP). */}
                            {entry.identifiant && (
                              <button
                                className="parking-vip-btn"
                                title="Créer une action : réception VIP, intervention atelier ou litige"
                                onClick={e => { e.stopPropagation(); setQuickActionEntry(entry); }}
                              >+</button>
                            )}
                          </div>
                        </td>
                        {/* Statut parking : retiré du tableau (2026-10-23). Il reste
                            modifiable dans la fiche véhicule, filtrable et exporté. */}
                        {/* Responsable : retiré du tableau (peu renseigné, alourdissait la
                            ligne). Il reste modifiable dans la fiche véhicule et filtrable. */}
                        <td style={{ whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                          {entry.or_date_creation ? (
                            entry.or_en_cours_id ? (
                              <span className="badge-or badge-or-clickable badge-or-date" title="Voir l'OR en cours" onClick={() => setOrModalId(entry.or_en_cours_id)}>
                                {formatDate(entry.or_date_creation)}
                                <svg className="badge-date-eye" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z"/><circle cx="12" cy="12" r="3"/></svg>
                              </span>
                            ) : formatDate(entry.or_date_creation)
                          ) : null}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                          {entry.statut_commande ? (
                            <span
                              className="badge-commande-clickable"
                              style={{
                                cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
                                background: entry.statut_commande === 'livre' ? 'light-dark(#dcfce7, var(--color-success-light))' : entry.statut_commande === 'recu_partiellement' ? 'light-dark(#fef3c7, var(--color-warning-light))' : 'light-dark(#dbeafe, var(--color-info-light))',
                                color: entry.statut_commande === 'livre' ? 'light-dark(#15803d, var(--color-success-dark))' : entry.statut_commande === 'recu_partiellement' ? 'light-dark(#b45309, var(--color-warning-dark))' : 'light-dark(#1d4ed8, var(--color-info-dark))',
                                border: `1px solid ${entry.statut_commande === 'livre' ? 'light-dark(#bbf7d0, var(--border-strong))' : entry.statut_commande === 'recu_partiellement' ? 'light-dark(#fde68a, var(--border-strong))' : 'light-dark(#bfdbfe, var(--border-strong))'}`,
                                fontSize: 11, padding: '3px 8px', borderRadius: 4, fontWeight: 600
                              }}
                              title={`${entry.nb_lignes_commande} ligne(s) — Cliquer pour le détail`}
                              onClick={() => { if (entry.num_or) setCommandeDetailOr(entry.num_or); }}
                            >
                              {entry.statut_commande === 'livre' ? 'Reçu' : entry.statut_commande === 'recu_partiellement' ? 'Partiel' : 'Commandé'}
                              {entry.nb_anomalies > 0 && <span title={`${entry.nb_anomalies} anomalie(s) active(s)`}> ⚠️</span>}
                              <svg className="badge-date-eye" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z"/><circle cx="12" cy="12" r="3"/></svg>
                            </span>
                          ) : (
                            null
                          )}
                        </td>
                        <td className="col-facture" onClick={e => e.stopPropagation()}>
                          {(() => {
                            const hideFacture = entry.derniere_facture_date && entry.or_date_creation && new Date(entry.or_date_creation) > new Date(entry.derniere_facture_date);
                            if (hideFacture || !entry.derniere_facture_date) return null;
                            const factureRef = facturesBadgeMap[(entry.identifiant || '').toUpperCase()];
                            return factureRef ? (
                              <span className="badge-facture badge-facture-clickable badge-facture-date" title="Voir la dernière facture atelier" onClick={() => { setFactureImmat(factureRef); setShowFactureDetail(true); }}>
                                {formatDate(entry.derniere_facture_date)}
                                <svg className="badge-date-eye" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z"/><circle cx="12" cy="12" r="3"/></svg>
                              </span>
                            ) : formatDate(entry.derniere_facture_date);
                          })()}
                        </td>
                        <td style={{ whiteSpace: 'nowrap', width: '1%', padding: '4px 8px' }}
                          onClick={e => { if (entry.planning_statut_nom && entry.planning_intervention_id && canOpenPlanning) e.stopPropagation(); }}>
                          {entry.planning_statut_nom ? (
                            // Cliquable comme la date de la colonne Planification (même
                            // dossier, même modale, mêmes droits) : ouvrir l'intervention
                            // depuis son statut est le geste naturel.
                            <span className={`parking-statut-atelier${entry.planning_intervention_id && canOpenPlanning ? ' parking-statut-atelier--btn' : ''}`}
                              role={entry.planning_intervention_id && canOpenPlanning ? 'button' : undefined}
                              tabIndex={entry.planning_intervention_id && canOpenPlanning ? 0 : undefined}
                              onClick={entry.planning_intervention_id && canOpenPlanning ? () => ouvrirDossierPlanning(entry) : undefined}
                              onKeyDown={entry.planning_intervention_id && canOpenPlanning ? (e => { if (e.key === 'Enter') ouvrirDossierPlanning(entry); }) : undefined}
                              style={{ background: entry.planning_statut_couleur || 'var(--bg-muted)', color: entry.planning_statut_text_color || '#000' }}
                              title={entry.planning_intervention_id && canOpenPlanning
                                ? 'Ouvrir le dossier du Planning Atelier'
                                : entry.planning_statut_since_estime
                                  ? 'Statut du Planning Atelier — date approchée (antérieure à l\'horodatage des statuts)'
                                  : 'Statut du Planning Atelier, et date de passage dans ce statut'}>
                              {entry.planning_statut_nom}
                              {(() => {
                                const l = libelleStatutDepuis(entry.planning_statut_nom, entry.planning_statut_since, entry.planning_statut_since_estime);
                                return l ? <span className="parking-statut-atelier-depuis"> {l}</span> : null;
                              })()}
                            </span>
                          ) : null}
                        </td>
                        <td onClick={e => e.stopPropagation()} style={{ fontSize: 11, whiteSpace: 'nowrap', width: '1%', padding: '4px 8px' }}>
                          <PlanificationCell dossier={planificationAVenir(entry) ? entry : { ...entry, date_planification: null }} onOuvrir={ouvrirDossierPlanning} cliquable={canOpenPlanning} />
                        </td>
                        {/* Colonnes VO et LIT retirées (2026-10-23) : la donnée reste,
                            pour les filtres « Stock VO » / « Avec litige », l'export et
                            la fiche du véhicule. */}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>

      </> /* fin parkingView === 'tableau' */}

      {/* ── Vue Carte ── */}
      {parkingView === 'carte' && (
        <ParkingCarteView
          entries={filteredEntries}
          statuts={statuts}
          onOpenEntry={entry => handleRowClick(entry)}
        />
      )}

      {/* ── Vue Rapport structuré ── */}
      {parkingView === 'rapport' && (
        <ParkingRapportView
          entries={filteredEntries}
          onRowClick={handleRowClick}
          statuts={statuts}
          responsables={responsables}
          concessionFilter={filters.concession}
          user={user}
          onRefresh={loadEntries}
          facturesBadgeMap={facturesBadgeMap}
          onStatusChange={handleUpdateStatutInline}
          onOuvrirDossier={ouvrirDossierPlanning}
        />
      )}

      {/* ── Modal édition véhicule (style Litiges) ── */}
      {showEditModal && selectedEntry && (() => {
        const navEntries = navEntriesRef.current;
        const selectedIdx = navEntries.findIndex(e => e.id === selectedEntry.id);
        const currentStatut = statuts.find(s => s.valeur === selectedEntry.statut);
        return (
        <div className="modal-overlay">
          <div className="modal-content large parking-detail-modal" onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="modal-header">
              <button
                className="modal-nav-btn"
                onClick={() => { if (selectedIdx > 0) handleNavEntry(navEntries[selectedIdx - 1]); }}
                disabled={selectedIdx <= 0}
                title="Véhicule précédent"
              >◀</button>
              <h2>🚗 Fiche véhicule</h2>
              <span className="modal-nav-counter">{selectedIdx + 1} / {navEntries.length}</span>
              <button
                className="modal-nav-btn"
                onClick={() => { if (selectedIdx < navEntries.length - 1) handleNavEntry(navEntries[selectedIdx + 1]); }}
                disabled={selectedIdx >= navEntries.length - 1}
                title="Véhicule suivant"
              >▶</button>
              <button className="modal-close" onClick={cancelEdit}>✕</button>
            </div>

            <div className="modal-body">

              {/* Layout : infos + actions à gauche | photo à droite */}
              <div className="parking-fiche-top">
                <div className="parking-fiche-info">

                  {/* Grille champs éditables */}
                  <div className="details-grid parking-edit-grid">
                    <div className="form-group-parking">
                      <label>Identifiant *</label>
                      <input
                        type="text"
                        value={editForm.identifiant}
                        onChange={e => setEditForm(f => ({ ...f, identifiant: e.target.value.replace(/[\s-]/g, '').toUpperCase() }))}
                        autoFocus
                        placeholder="Ex : AB123CD"
                      />
                    </div>
                    <div className="form-group-parking">
                      <label>Marque</label>
                      <input
                        type="text"
                        value={editForm.marque}
                        onChange={e => setEditForm(f => ({ ...f, marque: e.target.value }))}
                        placeholder="Ex : Peugeot"
                      />
                    </div>
                    <div className="form-group-parking">
                      <label>Emplacement</label>
                      <input
                        type="text"
                        value={editForm.allee}
                        onChange={e => setEditForm(f => ({ ...f, allee: e.target.value }))}
                        placeholder="Ex : Parking Client"
                      />
                    </div>
                    <div className="form-group-parking">
                      <label>Concession</label>
                      <span className="detail-value" style={{ paddingTop: '6px', display: 'block' }}>
                        {selectedEntry.concession || '—'}
                      </span>
                    </div>
                    <div className="form-group-parking">
                      <label>1ère détection</label>
                      <span className="detail-value" style={{ paddingTop: '6px', display: 'block' }}>
                        {formatDate(selectedEntry.created_at)}
                      </span>
                    </div>
                    <div className="form-group-parking">
                      <label>Ancienneté</label>
                      <span style={{ paddingTop: '6px', display: 'block' }}>
                        {(() => { const a = getAnciennete(selectedEntry.created_at); return a.value ? <span className={`age-chip ${a.cls}`}>{a.label}</span> : '-'; })()}
                      </span>
                    </div>
                    <div className="form-group-parking">
                      <label>Dernière actualisation</label>
                      <span className="detail-value" style={{ paddingTop: '6px', display: 'block' }}>
                        {selectedEntry.updated_at
                          ? new Date(selectedEntry.updated_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                          : '—'}
                      </span>
                    </div>
                    <div className="form-group-parking">
                      <label>Propriétaire</label>
                      <input
                        type="text"
                        list="parking-proprietaires"
                        value={editForm.proprietaire || ''}
                        onChange={e => setEditForm(f => ({ ...f, proprietaire: e.target.value }))}
                        placeholder="Nom du propriétaire"
                        autoComplete="off"
                      />
                      <datalist id="parking-proprietaires">
                        {proprietairesList.map(p => <option key={p} value={p} />)}
                      </datalist>
                    </div>
                  </div>

                  {/* Responsable */}
                  <div style={{ marginBottom: '8px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Responsable</div>
                    <select
                      className="parking-select"
                      value={String(selectedEntry.responsable_id || '')}
                      onChange={e => { handleUpdateResponsable(selectedEntry.id, e.target.value).catch(() => {}); }}
                    >
                      <option value="">— Non assigné —</option>
                      {[...responsables].sort((a, b) => a.nom.localeCompare(b.nom)).map(r => (
                        <option key={r.id} value={String(r.id)}>{r.nom}{r.prenom ? ' ' + r.prenom : ''}</option>
                      ))}
                    </select>
                    {responsables.length === 0 && (
                      <div style={{ fontSize: 11, color: 'var(--color-warning-dark, #b45309)', marginTop: 4 }}>
                        Aucun responsable actif — vérifier Référentiels → Responsables.
                      </div>
                    )}
                  </div>

                  {/* Statut + Action à mener — même ligne */}
                  <div style={{ display: 'flex', gap: '12px', marginBottom: '8px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Statut</div>
                      <div className="parking-statut-row">
                        <span className="statut-dot" style={{ background: currentStatut?.couleur || '#94a3b8' }} />
                        <select
                          className="parking-select"
                          value={selectedEntry.statut || ''}
                          onChange={e => handleQuickStatutChange(e.target.value)}
                          disabled={statusChanging}
                        >
                          {statuts.map(s => (
                            <option key={s.id} value={s.valeur}>{s.nom}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* OR */}
                  {(selectedEntry.or_en_cours_id) && (
                    <div className="parking-or-bloc">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ flex: 1 }}>
                          <div className="parking-or-header">🔩 OR</div>
                          {orEnCoursLoading ? (
                            <span style={{ fontSize: '13px' }}>Chargement…</span>
                          ) : orEnCoursDetail ? (
                            <>
                              <div style={{ fontSize: '13px', marginBottom: '2px' }}>
                                N° {orEnCoursDetail.num_or || orEnCoursDetail.id}
                                {(orEnCoursDetail.nom_client || orEnCoursDetail.client) ? ` — ${orEnCoursDetail.nom_client || orEnCoursDetail.client}` : ''}
                              </div>
                              {(orEnCoursDetail.date_creation || orEnCoursDetail.date_debut_travaux) && (
                                <div style={{ fontSize: '12px', color: 'var(--color-warning-dark)' }}>
                                  Ouverture : {formatDate(orEnCoursDetail.date_creation || orEnCoursDetail.date_debut_travaux)}
                                  {orEnCoursDetail.statut ? ` — ${orEnCoursDetail.statut}` : ''}
                                </div>
                              )}
                              {!(orEnCoursDetail.date_creation || orEnCoursDetail.date_debut_travaux) && orEnCoursDetail.statut && (
                                <div style={{ fontSize: '12px', color: 'var(--color-warning-dark)' }}>{orEnCoursDetail.statut}</div>
                              )}
                            </>
                          ) : null}
                        </div>
                        {!orEnCoursLoading && orEnCoursDetail && (
                          <button className="parking-or-btn" onClick={() => setOrModalId(selectedEntry.or_en_cours_id)}>
                            Voir l'OR →
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Litige en cours */}
                  {(selectedEntry.litige_id) && (
                    <div className="parking-litige-bloc">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ flex: 1 }}>
                          <div className="parking-litige-header">⚖️ Litige en cours</div>
                          {litigeLoading ? (
                            <span style={{ fontSize: '13px' }}>Chargement…</span>
                          ) : litigeDetail ? (
                            <>
                              <div style={{ fontSize: '13px', marginBottom: '2px' }}>
                                {litigeDetail.nom} {litigeDetail.prenom}
                              </div>
                              <div style={{ fontSize: '12px', color: 'light-dark(#9d174d, var(--color-decision))' }}>
                                {litigeDetail.statut_nom && `Statut : ${litigeDetail.statut_nom}`}
                                {litigeDetail.date_prochaine_action ? ` — Prochaine action : ${formatDate(litigeDetail.date_prochaine_action)}` : ''}
                              </div>
                            </>
                          ) : null}
                        </div>
                        {!litigeLoading && litigeDetail && (
                          <button className="parking-litige-btn" onClick={() => setLitigeModal(litigeDetail)}>
                            Voir le litige →
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Facture atelier */}
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      🧾 Dernière facture atelier
                    </div>
                    {factureImmatLoading ? (
                      <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Chargement…</span>
                    ) : factureImmat ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: 'var(--color-success-light)', border: '1px solid var(--color-success)', borderRadius: '6px' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: '13px', color: 'light-dark(var(--color-success), var(--color-success-dark))' }}>
                            {(() => { const d = factureImmat.date_facture; if (!d) return '—'; const [y,m,day] = d.split('T')[0].split('-').map(Number); return new Date(y, m-1, day).toLocaleDateString('fr-FR'); })()}
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {factureImmat.num_facture}
                            {factureImmat.nom_client ? ` · ${factureImmat.nom_client}` : ''}
                            {' · '}
                            <strong style={{ color: parseFloat(factureImmat.montant_ht) < 0 ? 'var(--color-error)' : 'var(--color-success)' }}>
                              {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(factureImmat.montant_ht)}
                            </strong>
                          </div>
                        </div>
                        <button
                          onClick={() => setShowFactureDetail(true)}
                          style={{ flexShrink: 0, padding: '5px 10px', background: 'var(--color-success)', color: 'var(--text-inverse)', border: 'none', borderRadius: '6px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
                        >
                          Voir →
                        </button>
                      </div>
                    ) : (
                      <span style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic' }}>Aucune facture pour cet immat</span>
                    )}
                  </div>

                </div>

                {/* Photo à droite */}
                <div
                  className="parking-fiche-photo"
                  onClick={e => openLightbox(e, selectedEntry.id)}
                  title="Voir la photo en grand"
                >
                  {modalPhotoLoading ? (
                    <span className="parking-fiche-photo-placeholder">📷 Chargement…</span>
                  ) : modalPhotoSrc ? (
                    <img src={`data:image/jpeg;base64,${modalPhotoSrc}`} alt="véhicule" />
                  ) : (
                    <span className="parking-fiche-photo-placeholder">📷 Pas de photo</span>
                  )}
                </div>
              </div>

            {/* Localisation GPS — pleine largeur, au-dessus des notes */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                📍 Localisation GPS
              </div>
              {selectedEntry.latitude && selectedEntry.longitude ? (
                <>
                  <iframe
                    title="Localisation véhicule"
                    className="parking-map-iframe"
                    src={`https://maps.google.com/maps?q=${selectedEntry.latitude},${selectedEntry.longitude}&z=19&t=k&output=embed`}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                  <a
                    href={`https://www.google.com/maps?q=${selectedEntry.latitude},${selectedEntry.longitude}`}
                    target="_blank"
                    rel="noreferrer"
                    className="parking-map-link"
                  >
                    Ouvrir dans Google Maps ↗
                  </a>
                </>
              ) : (
                <div style={{ fontSize: '13px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  Aucune coordonnée GPS enregistrée pour ce véhicule
                </div>
              )}
            </div>

              {/* Section notes / commentaires */}
              <div className="commentaires-section">
                <h3>📝 Notes {notes.length > 0 && <span className="notes-count-badge">{notes.length}</span>}</h3>

                <div className="commentaires-list">
                  {notesLoading ? (
                    <p style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Chargement…</p>
                  ) : notes.length === 0 ? (
                    <p style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Aucune note pour ce véhicule</p>
                  ) : (
                    notes.map(note => {
                      const ns = getNoteStatut(note.statut);
                      return (
                        <div key={note.id} className="commentaire-item" style={{ borderLeftColor: ns.color }}>
                          <div className="commentaire-header">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span className="note-badge" style={{ background: ns.bg, color: ns.color }}>{ns.label}</span>
                              <strong style={{ color: 'var(--text-primary)' }}>{note.prenom} {note.nom}</strong>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span className="commentaire-date">{formatNoteDate(note.created_at)}</span>
                              <button className="note-delete-btn" onClick={() => handleDeleteNote(note.id)} title="Supprimer">×</button>
                            </div>
                          </div>
                          <div className="commentaire-text">{note.contenu}</div>
                        </div>
                      );
                    })
                  )}
                </div>

                <div className="new-comment">
                  <textarea
                    value={noteForm.contenu}
                    onChange={e => setNoteForm(f => ({ ...f, contenu: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddNote(); } }}
                    placeholder="Ajouter une note… (Entrée pour valider)"
                    rows="2"
                  />
                  <button
                    className="btn-primary"
                    onClick={handleAddNote}
                    disabled={!noteForm.contenu.trim() || noteAdding}
                    style={{ alignSelf: 'flex-end' }}
                  >
                    {noteAdding ? 'Ajout…' : '✅ Ajouter'}
                  </button>
                </div>
              </div>

            </div>

            {editError && <div className="form-error-parking" style={{ marginBottom: '12px' }}>{editError}</div>}

            {/* Conflit d'identifiant — la fusion devient un geste explicite.
                On montre CE QU'ON VA PERDRE (la fiche courante) et ce qu'on
                garde, pour que la décision soit prise en connaissance de cause. */}
            {conflitFusion && (
              <div className="gp-fusion">
                <div className="gp-fusion-titre">
                  ⚠️ <b>{conflitFusion.identifiant}</b> est déjà porté par une autre fiche
                </div>
                <div className="gp-fusion-detail">
                  Fiche existante n° {conflitFusion.id}
                  {conflitFusion.allee ? ` · allée ${conflitFusion.allee}` : ''}
                  {conflitFusion.vue_le ? ` · vue le ${conflitFusion.vue_le}` : ''}
                  {conflitFusion.a_photo ? ' · avec photo' : ' · sans photo'}
                </div>
                <div className="gp-fusion-question">
                  S'agit-il du <b>même véhicule</b>, photographié deux fois ?
                </div>
                <div className="gp-fusion-actions">
                  <button type="button" className="btn-secondary" disabled={fusionEnCours}
                          onClick={() => setConflitFusion(null)}>
                    Non — corriger ma saisie
                  </button>
                  <button type="button" className="btn-primary" disabled={fusionEnCours}
                          onClick={confirmerFusion}>
                    {fusionEnCours ? 'Fusion…' : 'Oui, fusionner les deux fiches'}
                  </button>
                </div>
                <div className="gp-fusion-note">
                  La fiche courante sera supprimée au profit de l'existante, qui reprendra
                  la photo et la position les plus récentes. L'opération est tracée et réversible.
                </div>
              </div>
            )}

            <div className="modal-actions">
              {autoSaved && (
                <span style={{ fontSize: '12px', color: 'light-dark(var(--color-success), var(--color-success-dark))', marginRight: 'auto', fontWeight: '600' }}>
                  ✓ Sauvegardé
                </span>
              )}
              <button className="btn-secondary" onClick={cancelEdit}>Fermer</button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* ── Modale détail facture atelier ── */}
      {showFactureDetail && factureImmat && (
            <FactureDetailModal facture={factureImmat} onClose={() => setShowFactureDetail(false)} />
          )}

      {/* ── Modale export Excel : choix des champs ── */}
      {showExportModal && (
        <div className="modal-overlay">
          <div className="modal-content small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📊 Exporter en Excel</h2>
              <button className="modal-close" onClick={() => setShowExportModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-secondary)', marginBottom: '12px', fontSize: '14px' }}>
                Choisissez les colonnes à inclure. L'export reprend les <strong>{sortedEntries.length}</strong> entrée(s)
                affichée(s) selon les filtres et le tri en cours.
              </p>
              <div className="export-fields-actions">
                <button type="button" className="btn-link-parking" onClick={() => setAllExportFields(true)}>Tout cocher</button>
                <span className="export-fields-sep">·</span>
                <button type="button" className="btn-link-parking" onClick={() => setAllExportFields(false)}>Tout décocher</button>
              </div>
              <div className="export-fields-grid">
                {EXPORT_COLUMN_DEFS.map(([key, label]) => (
                  <label key={key} className="export-field-option">
                    <input
                      type="checkbox"
                      checked={!!exportFields[key]}
                      onChange={() => toggleExportField(key)}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowExportModal(false)}>Annuler</button>
              <button className="btn-primary" onClick={handleExportExcel} disabled={exportSelectedCount === 0}>
                Exporter ({exportSelectedCount})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modale modification groupée de statut ── */}
      {showBulkStatutModal && (
        <div className="modal-overlay">
          <div className="modal-content small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>✏️ Modifier le statut</h2>
              <button className="modal-close" onClick={() => { setShowBulkStatutModal(false); setBulkStatut(''); }}>✕</button>
            </div>
            <div className="modal-body">
            <p style={{ color: 'var(--text-secondary)', marginBottom: '16px', fontSize: '14px' }}>
              Appliquer un nouveau statut à <strong>{selectedIds.size}</strong> véhicule(s) sélectionné(s).
            </p>
            <div className="statut-quick-grid modal-statut-grid">
              {statuts.map(s => (
                <button
                  key={s.valeur}
                  className={`statut-quick-btn${bulkStatut === s.valeur ? ' active' : ''}`}
                  style={bulkStatut === s.valeur
                    ? { borderColor: s.couleur, background: s.couleur + '22', color: texteTheme(s.couleur) }
                    : {}}
                  onClick={() => setBulkStatut(s.valeur)}
                >
                  {s.nom}
                </button>
              ))}
            </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => { setShowBulkStatutModal(false); setBulkStatut(''); }}>
                Annuler
              </button>
              <button
                className="btn-primary"
                onClick={handleBulkEditStatut}
                disabled={!bulkStatut || bulkLoading}
              >
                {bulkLoading ? 'Modification…' : '✅ Appliquer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Lightbox photo ── */}
      {lightbox.open && (
        <div className="parking-lightbox-overlay" onClick={() => setLightbox({ open: false, src: null, loading: false })}>
          {lightbox.loading && <div className="parking-lightbox-loading">Chargement…</div>}
          {!lightbox.loading && lightbox.src && (
            <img src={`data:image/jpeg;base64,${lightbox.src}`} alt="Véhicule" onClick={e => e.stopPropagation()} />
          )}
          {!lightbox.loading && !lightbox.src && (
            <div className="parking-lightbox-loading">Pas de photo disponible</div>
          )}
        </div>
      )}

      {/* ── Modal ajout manuel ── */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Nouvelle entrée manuelle</h2>
              <button className="modal-close" onClick={closeAddModal}>✕</button>
            </div>
            <form onSubmit={handleFormSubmit}>
              <div className="modal-body">
                <div className="form-grid-parking">
                  <div className="form-group-parking">
                    <label>Identifiant *</label>
                    <input type="text" value={formData.identifiant}
                      onChange={e => setFormData(f => ({ ...f, identifiant: e.target.value.replace(/[\s-]/g, '').toUpperCase() }))}
                      placeholder="Ex : AB123CD" required autoFocus />
                  </div>
                  <div className="form-group-parking">
                    <label>Type *</label>
                    <select value={formData.type_identifiant}
                      onChange={e => setFormData(f => ({ ...f, type_identifiant: e.target.value }))}>
                      <option value="PLAQUE">Plaque</option>
                      <option value="VIN">VIN</option>
                    </select>
                  </div>
                  <div className="form-group-parking">
                    <label>Marque</label>
                    <input type="text" value={formData.marque}
                      onChange={e => setFormData(f => ({ ...f, marque: e.target.value }))}
                      placeholder="Ex : Peugeot" />
                  </div>
                  <div className="form-group-parking">
                    <label>Agent</label>
                    <input type="text" value={formData.agent}
                      onChange={e => setFormData(f => ({ ...f, agent: e.target.value }))}
                      placeholder="Nom de l'agent" />
                  </div>
                  <div className="form-group-parking">
                    <label>Emplacement</label>
                    <input type="text" value={formData.allee}
                      onChange={e => setFormData(f => ({ ...f, allee: e.target.value }))}
                      placeholder="Ex : A1" />
                  </div>
                </div>
                {formError && <div className="form-error-parking">{formError}</div>}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={closeAddModal}>Annuler</button>
                <button type="submit" className="btn-primary" disabled={formSaving}>
                  {formSaving ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal import Excel ── */}
      {showImportModal && (
        <div className="modal-overlay">
          <div className="modal-content small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Import Excel — Parking</h2>
              <button className="modal-close" onClick={closeImportModal}>✕</button>
            </div>

            {/* Étape 1 : Sélection scope + fichier */}
            {importStep === 1 && (
              <>
                <div className="modal-body">
                  <div className="form-grid-parking">
                    <div className="form-group-parking">
                      <label>Concession *</label>
                      <select
                        value={importForm.concession}
                        onChange={e => setImportForm(f => ({ ...f, concession: e.target.value, allee: '' }))}
                      >
                        <option value="">— Sélectionner —</option>
                        {concessionsList.map(c => (
                          <option key={c.id} value={c.nom}>{c.nom}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group-parking">
                      <label>Zone / Allée *</label>
                      <input
                        list="import-allee-options"
                        type="text"
                        value={importForm.allee}
                        onChange={e => setImportForm(f => ({ ...f, allee: e.target.value }))}
                        placeholder="Ex : Parking Client"
                      />
                      <datalist id="import-allee-options">
                        {alleesDispos.map(a => <option key={a} value={a} />)}
                      </datalist>
                    </div>
                  </div>

                  <div className="form-group-parking" style={{ marginTop: '12px' }}>
                    <label>Fichier Excel (.xlsx / .xls)</label>
                    <div className="import-file-panels">
                      <button
                        type="button"
                        className="import-choice-btn btn-download"
                        onClick={downloadParkingTemplate}
                      >
                        <span className="choice-icon">📄</span>
                        <span>Télécharger<br />le fichier modèle</span>
                      </button>
                      <button
                        type="button"
                        className="import-choice-btn btn-upload"
                        onClick={() => importFileRef.current?.click()}
                      >
                        <span className="choice-icon">📂</span>
                        <span>Charger<br />le fichier</span>
                      </button>
                    </div>
                    {importFilename && (
                      <div style={{ marginTop: '8px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                        📄 {importFilename} — {importRows.length} véhicule{importRows.length !== 1 ? 's' : ''} détecté{importRows.length !== 1 ? 's' : ''}
                      </div>
                    )}
                    <input
                      ref={importFileRef}
                      type="file"
                      accept=".xlsx,.xls"
                      style={{ display: 'none' }}
                      onChange={handleImportFileChange}
                    />
                  </div>

                  {importError && <div className="form-error-parking">{importError}</div>}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn-secondary" onClick={closeImportModal}>Annuler</button>
                  <button
                    type="button"
                    className="btn-primary"
                    disabled={!importForm.concession || !importForm.allee.trim() || importRows.length === 0}
                    onClick={() => setImportStep(2)}
                  >
                    Suivant →
                  </button>
                </div>
              </>
            )}

            {/* Étape 2 : Confirmation */}
            {importStep === 2 && (
              <>
                <div className="modal-body">
                  <p style={{ lineHeight: '1.6', margin: '8px 0 16px' }}>
                    Vous allez remplacer toutes les données de{' '}
                    <strong>{importForm.concession}</strong> — zone{' '}
                    <strong>{importForm.allee}</strong> avec{' '}
                    <strong>{importRows.length} véhicule{importRows.length !== 1 ? 's' : ''}</strong>.
                    <br />
                    Les véhicules absents de ce fichier seront marqués <strong>«&nbsp;À contrôler&nbsp;»</strong>.
                    <br /><br />
                    Confirmer l'import ?
                  </p>
                  {importError && <div className="form-error-parking">{importError}</div>}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn-secondary" onClick={() => { setImportStep(1); setImportError(''); }}>
                    ← Retour
                  </button>
                  <button
                    type="button"
                    className="btn-primary"
                    disabled={importLoading}
                    onClick={handleImportConfirm}
                  >
                    {importLoading ? 'Import en cours...' : '✅ Confirmer'}
                  </button>
                </div>
              </>
            )}

            {/* Étape 3 : Résultat */}
            {importStep === 3 && importResult && (
              <>
                <div className="modal-body">
                  <p style={{ lineHeight: '1.6', margin: '8px 0', color: 'var(--color-success-dark)', fontWeight: '600' }}>
                    ✅ Import terminé
                  </p>
                  <p style={{ margin: '8px 0', lineHeight: '1.6' }}>
                    {importResult.upserted} véhicule{importResult.upserted !== 1 ? 's' : ''} mis à jour
                    {importResult.marked_controler > 0 && (
                      <>, {importResult.marked_controler} marqué{importResult.marked_controler !== 1 ? 's' : ''} «&nbsp;À contrôler&nbsp;»</>
                    )}.
                  </p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn-primary" onClick={closeImportModal}>
                    Fermer
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {litigeModal && (
        <LitigeDetailsModal
          litige={litigeModal}
          onClose={() => setLitigeModal(null)}
          onCommentAdded={() => {}}
        />
      )}

      {orModalId && (
        <ORModal
          orId={orModalId}
          user={user}
          onClose={() => setOrModalId(null)}
        />
      )}

      {modaleDossier}

      {/* Modale détail commande pièces */}
      {commandeDetailOr && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 900 }}>
            <div className="modal-header">
              <h2>Détail commande — OR {commandeDetailOr}</h2>
              <button className="modal-close" onClick={() => setCommandeDetailOr(null)}>✕</button>
            </div>
            <div className="modal-body">
              {commandeDetailLoading ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Chargement...</div>
              ) : !commandeDetailData?.found || !commandeDetailData?.lignes?.length ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Aucune ligne de commande pour cet OR</div>
              ) : (() => {
                const lignes = commandeDetailData.lignes;
                const allLivre = lignes.every(l => l.statut_livraison === 'livre');
                const anyLivre = lignes.some(l => l.statut_livraison !== 'en_commande');
                const statutOr = allLivre ? 'livre' : anyLivre ? 'recu_partiellement' : 'en_commande';
                return (
                  <>
                    <div style={{ marginBottom: 12, fontSize: 13, color: 'var(--text-secondary)' }}>
                      <strong>{commandeDetailData.nom_client || '—'}</strong>
                      {commandeDetailData.immat && <span> — {commandeDetailData.immat}</span>}
                      <span style={{
                        marginLeft: 8, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                        background: statutOr === 'livre' ? 'light-dark(#dcfce7, var(--color-success-light))' : statutOr === 'recu_partiellement' ? 'light-dark(#fef3c7, var(--color-warning-light))' : 'light-dark(#dbeafe, var(--color-info-light))',
                        color: statutOr === 'livre' ? 'light-dark(#15803d, var(--color-success-dark))' : statutOr === 'recu_partiellement' ? 'light-dark(#b45309, var(--color-warning-dark))' : 'light-dark(#1d4ed8, var(--color-info-dark))',
                      }}>
                        {statutOr === 'livre' ? '✓ Tout reçu' : statutOr === 'recu_partiellement' ? '◐ Reçu partiellement' : '⏳ En commande'}
                      </span>
                      <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-muted)' }}>{lignes.length} ligne(s)</span>
                    </div>
                    <div className="table-container">
                      <table>
                        <thead>
                          <tr>
                            <th>Référence</th>
                            <th>Désignation</th>
                            <th>Fournisseur</th>
                            <th style={{ textAlign: 'center' }}>Qté cdée</th>
                            <th style={{ textAlign: 'center' }}>Qté livrée</th>
                            <th>Statut</th>
                            <th>Anomalie</th>
                          </tr>
                        </thead>
                        <tbody>
                          {lignes.map((l, i) => (
                            <tr key={i}>
                              <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{l.reference || '—'}</td>
                              <td>{l.designation || l.description || '—'}</td>
                              <td>{l.fournisseur || '—'}</td>
                              <td style={{ textAlign: 'center' }}>{l.cde_qty || 0}</td>
                              <td style={{ textAlign: 'center' }}>{l.qte_livree || 0}</td>
                              <td>
                                <span style={{
                                  fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 4,
                                  background: l.statut_livraison === 'livre' ? 'light-dark(#dcfce7, var(--color-success-light))' : l.statut_livraison === 'livre_partiellement' ? 'light-dark(#fef3c7, var(--color-warning-light))' : 'light-dark(#dbeafe, var(--color-info-light))',
                                  color: l.statut_livraison === 'livre' ? 'light-dark(#15803d, var(--color-success-dark))' : l.statut_livraison === 'livre_partiellement' ? 'light-dark(#b45309, var(--color-warning-dark))' : 'light-dark(#1d4ed8, var(--color-info-dark))',
                                }}>
                                  {l.statut_livraison === 'livre' ? '✓ Reçu' : l.statut_livraison === 'livre_partiellement' ? '◐ Partiel' : '⏳ Commandé'}
                                </span>
                              </td>
                              <td>
                                {l.anomalie_active ? (
                                  <div style={{ fontSize: 11 }}>
                                    <span style={{ background: 'var(--color-error-light)', color: 'var(--color-error-dark)', padding: '2px 6px', borderRadius: 4, fontWeight: 600, border: '1px solid light-dark(#fecaca, var(--border-strong))' }}>
                                      ⚠️ {l.anomalie_active.type || 'Anomalie'}
                                    </span>
                                    {l.anomalie_active.commentaire && (
                                      <div style={{ color: 'var(--text-secondary)', marginTop: 3, fontSize: 10, lineHeight: 1.3 }}>{l.anomalie_active.commentaire}</div>
                                    )}
                                  </div>
                                ) : (
                                  <span style={{ color: 'var(--text-muted)' }}>—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {printMode === 'fiches' && (
        <ParkingPrint
          entries={sortedEntries}
          statuts={statuts}
          responsables={responsables}
          onClose={() => setPrintMode(null)}
        />
      )}

      {printMode === 'tableau' && (
        <ParkingTablePrint
          entries={sortedEntries}
          statuts={statuts}
          responsables={responsables}
          isSuperAdmin={isSuperAdmin}
          onClose={() => setPrintMode(null)}
        />
      )}

      {/* ── Choix d'action rapide (bouton +) ─────────────────────────────────
          Le « + » créait un dossier Réception VIP sans le dire. Il propose
          désormais explicitement ses actions ; la liste est faite pour
          s'étendre (devis, litige, courtoisie…). */}
      {quickActionEntry && !showInterventionModal && (
        <div className="modal-overlay">
          <div className="modal-content small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Que faire avec ce véhicule ?</h2>
              <button className="modal-close" onClick={() => setQuickActionEntry(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="pk-quick-vehicule">
                <span className="pk-quick-immat">{quickActionEntry.identifiant}</span>
                <span className="pk-quick-desc">
                  {quickActionEntry.marque || 'Véhicule'}
                  {quickActionEntry.nom_client ? ` · ${quickActionEntry.nom_client}` : ''}
                </span>
              </div>
              <div className="pk-quick-actions">
                <button
                  type="button"
                  className="pk-quick-action"
                  onClick={() => { const e = quickActionEntry; setQuickActionEntry(null); handleCreateVip(e); }}
                >
                  <span className="pk-quick-picto">🏁</span>
                  <span className="pk-quick-txt">
                    <strong>Réception VIP</strong>
                    <small>Créer le dossier d'accueil personnalisé</small>
                  </span>
                </button>
                <button
                  type="button"
                  className="pk-quick-action"
                  disabled={planningRefsLoading}
                  onClick={() => openInterventionFromParking(quickActionEntry)}
                >
                  <span className="pk-quick-picto">📅</span>
                  <span className="pk-quick-txt">
                    <strong>{planningRefsLoading ? 'Chargement…' : 'Intervention atelier'}</strong>
                    <small>Planifier une intervention, sans quitter le parking</small>
                  </span>
                </button>
                {/* Litige : ouvre la page Litiges sur la création, pré-remplie avec la
                    ligne. Proposé seulement à qui a accès aux Litiges ; grisé si un
                    litige est déjà en cours, pour ne pas créer de doublon. */}
                {onCreerLitige && (
                  <button
                    type="button"
                    className="pk-quick-action is-bloque"
                    disabled={!!quickActionEntry.litige_id}
                    onClick={() => {
                      const e = quickActionEntry;
                      setQuickActionEntry(null);
                      onCreerLitige({
                        immatriculation: (e.identifiant || '').toUpperCase().replace(/[\s\-_]/g, ''),
                        marque: (e.marque || '').toUpperCase(),
                        nom: (e.nom_client || '').toUpperCase(),
                        concession_nom: e.concession || '',
                      });
                    }}
                  >
                    <span className="pk-quick-picto">⚖️</span>
                    <span className="pk-quick-txt">
                      <strong>Litige</strong>
                      <small>{quickActionEntry.litige_id
                        ? 'Un litige est déjà en cours pour ce véhicule (colonne LIT)'
                        : 'Créer un litige pré-rempli dans la page Litiges'}</small>
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Modale d'intervention, montée ICI : le réceptionnaire reste sur le
          parking. Mêmes référentiels que le Planning, pré-remplie depuis le
          véhicule sélectionné. */}
      {showInterventionModal && planningRefs && (
        <InterventionModal
          show={showInterventionModal}
          onClose={() => { setShowInterventionModal(false); setQuickActionEntry(null); }}
          onSaved={() => { setShowInterventionModal(false); setQuickActionEntry(null); }}
          user={user}
          editMode={editMode}
          statutsAtelier={planningRefs.statutsAtelier}
          travauxTypes={planningRefs.travauxTypes}
          mecaniciens={planningRefs.mecaniciens}
          receptionnaires={planningRefs.receptionnaires}
          initialFormOverrides={{
            immatriculation: (quickActionEntry?.identifiant || '').toUpperCase().replace(/[\s\-_]/g, ''),
            marque: (quickActionEntry?.marque || '').toUpperCase(),
            // `inventaire_parking` ne stocke pas le modèle : le réceptionnaire le
            // complète dans la modale (ou le récupère via la recherche Datacar).
            client_nom: (quickActionEntry?.nom_client || '').toUpperCase(),
          }}
          onSummary={() => {}}
        />
      )}
    </div>
  );
}

export default GestionParking;
